import type { UniversalNodeDefinition } from '../components/nodes';
import type { ModelOption, Parameter } from '../components/ui';
import { MODEL_REGISTRY } from '../constants/modelConfigs';

/**
 * Helper function to create a node definition
 */
export function createNodeDefinition(
  config: Omit<UniversalNodeDefinition, 'id'>
): UniversalNodeDefinition {
  return {
    id: `${config.type}-${Date.now()}`,
    ...config,
  };
}

/**
 * Example Node Definitions
 * These are kept for reference/testing - not used in production
 */

// Text Input Node
export const TEXT_INPUT_NODE: UniversalNodeDefinition = {
  id: 'text-input',
  type: 'TextInput',
  title: 'Text Input',
  category: 'source',
  variant: 'positive',
  outputsMediaType: 'text',
  hasInputHandle: false,
  hasOutputHandle: true,
  components: [
    {
      id: 'prompt-input',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      props: {
        placeholder: 'Enter your prompt...',
        variant: 'positive',
        showSubmitButton: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// AI Model Node
export const AI_MODEL_NODE: UniversalNodeDefinition = {
  id: 'ai-model',
  type: 'AIModel',
  title: 'AI Model',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'text',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  components: [
    {
      id: 'model-selector',
      type: 'ModelSelector',
      valueKey: 'selectedModel',
      onChangeKey: 'onModelChange',
      props: {
        label: 'Select Model',
        variant: 'purple',
        value: '',
        onChange: () => {},
        models: [
          {
            id: 'gemini-2.0-flash',
            name: 'Gemini 2.0 Flash',
            provider: 'Google',
            mediaTypes: ['text', 'image'],
          },
          {
            id: 'gemini-1.5-pro',
            name: 'Gemini 1.5 Pro',
            provider: 'Google',
            mediaTypes: ['text', 'image'],
          },
        ] as ModelOption[],
      },
    },
    {
      id: 'parameters',
      type: 'ParameterControls',
      valueKey: 'parameters',
      onChangeKey: 'onParameterChange',
      props: {
        variant: 'purple',
        values: {},
        onChange: () => {},
        parameters: [
          {
            id: 'temperature',
            label: 'Temperature',
            type: 'slider',
            min: 0,
            max: 2,
            step: 0.1,
            default: 1.0,
          },
          {
            id: 'maxTokens',
            label: 'Max Tokens',
            type: 'number',
            min: 1,
            max: 8192,
            default: 2048,
          },
        ] as Parameter[],
      },
    },
  ],
};

// Output Display Node
export const OUTPUT_DISPLAY_NODE: UniversalNodeDefinition = {
  id: 'output-display',
  type: 'OutputDisplay',
  title: 'Output',
  category: 'output',
  variant: 'neutral',
  acceptsMediaTypes: ['text', 'image', 'video'],
  hasInputHandle: true,
  hasOutputHandle: false,
  components: [
    {
      id: 'output-preview',
      type: 'MarkdownTextarea',
      valueKey: 'output',
      props: {
        placeholder: 'Output will appear here...',
        variant: 'neutral',
        readOnly: true,
        minHeight: '150px',
        maxHeight: '500px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

/**
 * Node Definitions Registry
 * JSON-based node configurations for the universal node architecture
 * 
 * Models are centralized in src/constants/modelConfigs.ts
 * Use MODEL_REGISTRY for model selector configurations
 */

// Starting Prompt Node
export const STARTING_PROMPT_NODE_DEF: UniversalNodeDefinition = {
  id: 'starting-prompt',
  type: 'StartingPrompt',
  title: 'Starting Prompt',
  category: 'source',
  variant: 'positive',
  hidden: true, // Hidden - use Writer node instead (it works the same without input)
  outputsMediaType: 'text',
  hasInputHandle: false,
  hasOutputHandle: true,
  components: [
    {
      id: 'prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter your prompt here... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Agent Prompt Node
export const AGENT_PROMPT_NODE_DEF: UniversalNodeDefinition = {
  id: 'agent-prompt',
  type: 'AgentPrompt',
  title: 'Text to Text',
  category: 'processor',
  variant: 'positive',
  acceptsMediaTypes: ['text', 'image'], // Accept both text and images
  outputsMediaType: 'text',
  maxInputConnections: 10, // GPT-4o can handle multiple images and text inputs
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'GPT Model',
      type: 'model',
      models: [
        // GPT-5.6 (July 2026) is the current generation. Removed from this
        // list but kept in MODEL_REGISTRY for saved flows: gpt-5.5, gpt-5.4,
        // gpt-5.4-pro and gpt-5.2 (superseded, same price as their successors)
        // and gpt-5-nano / gpt-5-mini (hard shutdown 2026-12-11).
        MODEL_REGISTRY['gpt-5.6-sol'],
        MODEL_REGISTRY['gpt-5.6-terra'],
        MODEL_REGISTRY['gpt-5.6-luna'],
        MODEL_REGISTRY['gpt-5.5-pro'],
        MODEL_REGISTRY['gpt-5.4-mini'],
        MODEL_REGISTRY['gpt-5.4-nano'],
      ],
      default: 'gpt-5.4-nano',
    },
  ],
  components: [
    {
      id: 'agent-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// ChatGPT Node (simplified version without GPT-5)
export const CHATGPT_NODE_DEF: UniversalNodeDefinition = {
  id: 'chatgpt',
  type: 'ChatGPT',
  title: 'ChatGPT',
  category: 'processor',
  variant: 'positive',
  acceptsMediaTypes: ['text', 'image'], // Accept both text and images
  outputsMediaType: 'text',
  maxInputConnections: 10, // GPT-4o can handle multiple images and text inputs
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'GPT Model',
      type: 'model',
      models: [
        // GPT-5.6 (July 2026) is the current generation. Removed from this
        // list but kept in MODEL_REGISTRY for saved flows: gpt-5.5, gpt-5.4,
        // gpt-5.4-pro and gpt-5.2 (superseded, same price as their successors)
        // and gpt-5-nano / gpt-5-mini (hard shutdown 2026-12-11).
        MODEL_REGISTRY['gpt-5.6-sol'],
        MODEL_REGISTRY['gpt-5.6-terra'],
        MODEL_REGISTRY['gpt-5.6-luna'],
        MODEL_REGISTRY['gpt-5.5-pro'],
        MODEL_REGISTRY['gpt-5.4-mini'],
        MODEL_REGISTRY['gpt-5.4-nano'],
      ],
      default: 'gpt-5.4-nano',
    },
  ],
  components: [
    {
      id: 'chatgpt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Claude Node (Anthropic)
export const CLAUDE_NODE_DEF: UniversalNodeDefinition = {
  id: 'claude',
  type: 'Claude',
  title: 'Claude',
  category: 'processor',
  variant: 'positive',
  acceptsMediaTypes: ['text', 'image'], // Claude supports vision
  outputsMediaType: 'text',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Claude Model',
      type: 'model',
      models: [
        // Current generation (July 2026)
        MODEL_REGISTRY['claude-opus-5'],
        MODEL_REGISTRY['claude-sonnet-5'],
        MODEL_REGISTRY['claude-haiku-4-5'],
        // Previous generations — kept so saved flows that pin them keep working
        MODEL_REGISTRY['claude-opus-4-8'],
        MODEL_REGISTRY['claude-opus-4-7'],
        MODEL_REGISTRY['claude-opus-4-6'],
        MODEL_REGISTRY['claude-sonnet-4-6'],
        MODEL_REGISTRY['claude-sonnet-4-5'],
      ],
      // Haiku stays the default: cheapest per generation (2 cr vs 5/15) and the
      // right floor for a node users drop on the canvas casually.
      default: 'claude-haiku-4-5',
    },
  ],
  components: [
    {
      id: 'claude-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Kimi Node (Moonshot AI via Ollama Cloud)
export const KIMI_NODE_DEF: UniversalNodeDefinition = {
  id: 'kimi',
  type: 'Kimi',
  title: 'Kimi',
  category: 'processor',
  variant: 'positive',
  acceptsMediaTypes: ['text', 'image'], // Kimi K2.6 supports vision
  outputsMediaType: 'text',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Kimi Model',
      type: 'model',
      models: [
        MODEL_REGISTRY['kimi-k2.6'],
        // 'kimi-k2.5' removed: retired by Ollama 2026-07-31
        // (https://ollama.com/library/kimi-k2.5). Still in MODEL_REGISTRY and
        // aliased to kimi-k2.6:cloud server-side so saved flows keep running.
        // Prices identically (3 cr), so the alias cannot mis-charge.
      ],
      default: 'kimi-k2.6',
    },
  ],
  components: [
    {
      id: 'kimi-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Qwen Node (Alibaba via Ollama Cloud)
export const QWEN_NODE_DEF: UniversalNodeDefinition = {
  id: 'qwen',
  type: 'Qwen',
  title: 'Qwen',
  category: 'processor',
  variant: 'positive',
  // Qwen 3.5 on Ollama Cloud carries the `vision` capability tag.
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'text',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Qwen Model',
      type: 'model',
      models: [
        MODEL_REGISTRY['qwen-3.5'],
        MODEL_REGISTRY['qwen-3.5-397b'],
        // 'qwen-3-vl-235b' and 'qwen-3-coder-next' were removed: their Ollama
        // Cloud tags (qwen3-vl:235b-cloud / qwen3-coder-next:cloud) do not
        // exist, so picking them silently ran qwen3.5 anyway. They remain in
        // MODEL_REGISTRY and are aliased server-side for saved flows.
      ],
      default: 'qwen-3.5',
    },
  ],
  components: [
    {
      id: 'qwen-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// MiniMax Node (via Ollama Cloud)
export const MINIMAX_NODE_DEF: UniversalNodeDefinition = {
  id: 'minimax',
  type: 'Minimax',
  title: 'MiniMax',
  category: 'processor',
  variant: 'positive',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'text',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'MiniMax Model',
      type: 'model',
      models: [
        MODEL_REGISTRY['minimax-m2.7'],
        // 'minimax-m2.5' removed: retired by Ollama 2026-07-31
        // (https://ollama.com/library/minimax-m2.5).
        // 'minimax-m2.1' removed: retired by Ollama 2026-07-15.
        // 'minimax-m2' was never in this dropdown; retired 2026-06-16.
        // All three stay in MODEL_REGISTRY and are aliased to m2.7 server-side
        // for saved flows. Every MiniMax id prices at 3 cr, so the aliases
        // cannot mis-charge.
      ],
      default: 'minimax-m2.7',
    },
  ],
  components: [
    {
      id: 'minimax-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Image Prompt Node (Gemini)
export const IMAGE_PROMPT_NODE_DEF: UniversalNodeDefinition = {
  id: 'image-prompt',
  type: 'ImagePrompt',
  title: 'Gemini 🍌',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 3, // Accept up to 3 image inputs
  hasInputHandle: true,
  hasOutputHandle: true,
  // Settings panel configuration
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'select',
      options: [
        { value: 'gemini-3-pro-image-preview', label: 'Banana Pro (Quality)' },
        { value: 'gemini-3.1-flash-image', label: 'Banana 2 (Fast)' },
        { value: 'gemini-3.1-flash-lite-image', label: 'Banana 2 Lite (Fastest)' },
        { value: 'gemini-nano-banana', label: 'Banana (Stable)' },
      ],
      default: 'gemini-3.1-flash-image',
      infoTooltip: 'Select the Gemini image model variant. Banana Pro is highest quality.',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '512px', label: '512px (Base)' },
        { value: '1024px', label: '1024px (HD)' },
        { value: '2048px', label: '2048px (2K)' },
        { value: '4096px', label: '4096px (4K)' },
      ],
      default: '1024px',
      infoTooltip: 'Output image resolution. Higher = more credits.',
      conditionalOptions: {
        dependsOn: 'selectedModel',
        rules: {
          'gemini-nano-banana': [
            { value: '1024px', label: '1024px (HD)' },
          ],
          'gemini-3-pro-image-preview': [
            { value: '1024px', label: '1024px (HD)' },
            { value: '2048px', label: '2048px (2K)' },
            { value: '4096px', label: '4096px (4K)' },
          ],
          // Banana 2 Lite generates at 1K only — no 0.5K, no 2K, no 4K.
          'gemini-3.1-flash-lite-image': [
            { value: '1024px', label: '1024px (HD)' },
          ],
          'gemini-3.1-flash-image': [
            { value: '512px', label: '512px (Base)' },
            { value: '1024px', label: '1024px (HD)' },
            { value: '2048px', label: '2048px (2K)' },
            { value: '4096px', label: '4096px (4K)' },
          ],
        },
      },
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '9:16',
      infoTooltip: 'Output image aspect ratio',
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom width in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom height in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'image-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image you want to generate...',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
    // ParameterControls removed - now using settings panel
  ],
};

// GPT Image Node (OpenAI)
export const GPT_IMAGE_NODE_DEF: UniversalNodeDefinition = {
  id: 'gpt-image',
  type: 'GPTImage',
  title: 'GPT Image',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 5, // GPT Image preserves first 5 images with higher fidelity
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model',
      type: 'select',
      options: [
        { value: 'gpt-image-2', label: 'GPT Image 2 (New)' },
        { value: 'gpt-image-1.5', label: 'GPT Image 1.5' },
        { value: 'gpt-image-1-mini', label: 'GPT Image 1 Mini (Cheapest)' },
      ],
      default: 'gpt-image-2',
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
        { value: '3:2', label: '3:2 Landscape' },
        { value: '2:3', label: '2:3 Portrait' },
      ],
      default: '1:1',
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom width in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom height in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'quality',
      label: 'Quality',
      type: 'select',
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
      default: 'medium',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'gpt-image-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image you want to generate...',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// FLUX Image Generator Node (Together AI)
export const FLUX_IMAGE_NODE_DEF: UniversalNodeDefinition = {
  id: 'flux-image',
  type: 'FluxImage',
  title: 'FLUX',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 3, // Accept up to 3 reference images
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'FLUX Model',
      type: 'select',
      options: [
        { value: 'flux-2-max', label: 'FLUX 2 Max (Best Quality)' },
        { value: 'flux-2-pro', label: 'FLUX 2 Pro (Multi-Ref Edit)' },
        { value: 'flux-2-turbo', label: 'FLUX 2 Turbo (Fast)' },
        { value: 'flux-2-flash', label: 'FLUX 2 Flash (Fastest)' },
      ],
      default: 'flux-2-flash',
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '1:1',
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom width in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom height in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'quality',
      label: 'Quality',
      type: 'select',
      options: [
        { value: 'fast', label: 'Fast (4 steps)' },
        { value: 'balanced', label: 'Balanced (20 steps)' },
        { value: 'high', label: 'High (28 steps)' },
      ],
      default: 'balanced',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'flux-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image you want to generate...',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// ============================================================================
// fal.ai Image Generation Nodes
// ============================================================================

// Z-Image Turbo Node (fal.ai) - Super fast text-to-image and image-to-image
export const Z_IMAGE_TURBO_NODE_DEF: UniversalNodeDefinition = {
  id: 'z-image-turbo',
  type: 'ZImageTurbo',
  title: 'Z-Image Turbo',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 1, // Accepts 1 image for image-to-image
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '1:1',
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom width in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom height in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'quality',
      label: 'Quality',
      type: 'select',
      options: [
        { value: 'fast', label: 'Fast (4 steps)' },
        { value: 'balanced', label: 'Balanced (8 steps)' },
        { value: 'high', label: 'High (12 steps)' },
      ],
      default: 'balanced',
    },
    {
      id: 'enablePromptExpansion',
      label: 'Prompt Expansion',
      type: 'toggle',
      default: false,
    },
    {
      id: 'strength',
      label: 'Image Strength',
      type: 'slider',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.75,
      description: 'How much to preserve the original image (only used for image-to-image)',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'z-image-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image you want to generate...',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Qwen Image Node (fal.ai) - Text-to-image and image-to-image editing
export const QWEN_IMAGE_NODE_DEF: UniversalNodeDefinition = {
  id: 'qwen-image',
  type: 'QwenImage',
  title: 'Qwen Image',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'model',
      models: [
        { id: 'qwen-image-2-pro', name: 'Qwen Image 2.0 Pro', description: 'Highest-fidelity tier, native 2048×2048' },
        { id: 'qwen-image-2', name: 'Qwen Image 2.0', description: 'Native 2K, fast generation' },
        { id: 'qwen-image', name: 'Qwen Image 1.0', description: 'Multi-image editing, quality control' },
      ],
      default: 'qwen-image-2',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '1:1',
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom width in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom height in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'quality',
      label: 'Quality',
      type: 'select',
      options: [
        { value: 'turbo', label: 'Turbo (10 steps)' },
        { value: 'balanced', label: 'Balanced (30 steps)' },
        { value: 'high', label: 'High (50 steps)' },
      ],
      default: 'balanced',
      showWhen: { field: 'selectedModel', value: 'qwen-image' },
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'qwen-image-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image you want to generate...',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Qwen Image Edit Node (fal.ai) - Image-to-image editing
export const QWEN_IMAGE_EDIT_NODE_DEF: UniversalNodeDefinition = {
  id: 'qwen-image-edit',
  type: 'QwenImageEdit',
  title: 'Qwen Edit',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['image'],  // Requires image input
  outputsMediaType: 'image',
  maxInputConnections: 1,  // Single image input
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'quality',
      label: 'Quality',
      type: 'select',
      options: [
        { value: 'fast', label: 'Fast (15 steps)' },
        { value: 'balanced', label: 'Balanced (30 steps)' },
        { value: 'high', label: 'High (50 steps)' },
      ],
      default: 'balanced',
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'qwen-edit-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe how to edit the image... e.g. "Change the background to a sunset"',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Seedream Node (fal.ai) - ByteDance stylized image generation and editing
export const SEEDREAM_NODE_DEF: UniversalNodeDefinition = {
  id: 'seedream',
  type: 'Seedream',
  title: 'Seedream',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 10, // Accepts up to 10 images for multi-image editing
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'model',
      models: [
        { id: 'seedream-5-lite', name: 'Seedream 5.0 Lite', description: 'Web search + multi-step reasoning' },
        { id: 'seedream-4.5', name: 'Seedream 4.5', description: 'Stylized generation with text rendering' },
      ],
      default: 'seedream-5-lite',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '1:1',
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom width in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom height in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'seedream-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image you want to generate... Supports text rendering!',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Kling Image Node (fal.ai) — Kuaishou Kling Image O3.
//
// Endpoints: fal-ai/kling-image/o3/{text-to-image,image-to-image}
// The i2i variant takes up to 10 reference images addressed positionally as
// @Image1..@Image10 in the prompt; server/routes/falai.ts injects that
// enumeration automatically when the user hasn't written it themselves.
//
// Two schema options are deliberately NOT exposed as settings:
//   - num_images (1-9) and result_type:'series' would return several images per
//     API call while credits are reserved with units=1 — i.e. N images for the
//     price of one. Multiples go through batchCount, which reserves per call.
//   - `elements` (per-character face control) needs per-image UI, not a select.
export const KLING_IMAGE_NODE_DEF: UniversalNodeDefinition = {
  id: 'kling-image',
  type: 'KlingImage',
  title: 'Kling Image',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 10, // fal.ai hard cap on Kling reference images
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'model',
      models: [
        { id: 'kling-image-o3', name: 'Kling Image O3', description: 'Up to 4K, multi-reference editing' },
      ],
      default: 'kling-image-o3',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: 'auto', label: 'Auto (match reference)' },
        { value: '1:1', label: '1:1 Square' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
        { value: '3:2', label: '3:2 Landscape' },
        { value: '2:3', label: '2:3 Portrait' },
        { value: '21:9', label: '21:9 Ultrawide' },
      ],
      default: '1:1',
      infoTooltip: 'Auto only applies when reference images are connected; text-to-image falls back to 16:9.',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '1K', label: '1K' },
        { value: '2K', label: '2K' },
        { value: '4K', label: '4K (2x credits)' },
      ],
      default: '1K',
      infoTooltip: 'fal.ai charges the same for 1K and 2K, and double for 4K — so 2K is free quality.',
    },
    {
      id: 'outputFormat',
      label: 'Output Format',
      type: 'select',
      options: [
        { value: 'png', label: 'PNG' },
        { value: 'jpeg', label: 'JPEG' },
        { value: 'webp', label: 'WebP' },
      ],
      default: 'png',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10). Each one is charged separately.',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'kling-image-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image... Connect reference images and refer to them as @Image1, @Image2.',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// GLM-Image Node (fal.ai) - Zhipu AI, excellent text rendering
export const GLM_IMAGE_NODE_DEF: UniversalNodeDefinition = {
  id: 'glm-image',
  type: 'GLMImage',
  title: 'GLM Image',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'image',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '1:1',
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 4,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-4)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'glm-image-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your image... GLM excels at text rendering and typography in images.',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Recraft V3 Node (fal.ai) - Text rendering and vector art
export const RECRAFT_V3_NODE_DEF: UniversalNodeDefinition = {
  id: 'recraft-v3',
  type: 'RecraftV3',
  title: 'Recraft V3',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'image',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'model',
      models: [
        { id: 'recraft-v4-pro', name: 'Recraft V4 Pro', description: 'Print-ready 4MP, premium quality' },
        { id: 'recraft-v4', name: 'Recraft V4', description: 'Fast 1MP, production-ready' },
        { id: 'recraft-v3', name: 'Recraft V3', description: 'Legacy V3, text + vector art' },
      ],
      default: 'recraft-v4',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '1:1', label: '1:1 Square' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '1:1',
    },
    {
      id: 'bannerPreset',
      label: 'Banner Preset',
      type: 'select',
      hidden: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'google-300x250', label: 'Google 300x250 (Medium Rectangle)' },
        { value: 'google-336x280', label: 'Google 336x280 (Large Rectangle)' },
        { value: 'google-728x90', label: 'Google 728x90 (Leaderboard)' },
        { value: 'google-970x90', label: 'Google 970x90 (Large Leaderboard)' },
        { value: 'google-300x600', label: 'Google 300x600 (Half Page)' },
        { value: 'google-160x600', label: 'Google 160x600 (Wide Skyscraper)' },
        { value: 'google-320x50', label: 'Google 320x50 (Mobile Banner)' },
        { value: 'google-320x100', label: 'Google 320x100 (Large Mobile Banner)' },
        { value: 'meta-1080x1080', label: 'Meta 1080x1080 (Square)' },
        { value: 'meta-1080x1350', label: 'Meta 1080x1350 (4:5 Feed)' },
        { value: 'meta-1080x1920', label: 'Meta 1080x1920 (Stories/Reels)' },
        { value: 'social-1200x628', label: 'Social 1200x628 (1.91:1)' },
        { value: 'linkedin-1200x627', label: 'LinkedIn 1200x627' },
      ],
      default: 'none',
      infoTooltip: 'Apply a standard banner size preset (sets custom width/height)'
    },
    {
      id: 'customWidth',
      label: 'Width (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom width in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'customHeight',
      label: 'Height (px)',
      type: 'number',
      hidden: true,
      min: 256,
      max: 4096,
      step: 8,
      default: 1024,
      infoTooltip: 'Custom height in pixels (only used when aspect ratio is Custom)',
      showWhen: { field: 'aspectRatio', value: 'custom' },
    },
    {
      id: 'style',
      label: 'Style',
      type: 'select',
      options: [
        { value: 'realistic', label: 'Realistic' },
        { value: 'digital', label: 'Digital Illustration' },
        { value: 'vector', label: 'Vector (2x cost)' },
      ],
      default: 'realistic',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'recraft-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image... Great for text in images and vector art!',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Grok Image Node (xAI direct API) - High-quality aesthetic image generation
// Supports text-to-image and image-to-image editing
const GROK_IMAGE_ASPECT_RATIOS = [
  { value: '1:1', label: '1:1 Square' },
  { value: '9:16', label: '9:16 Portrait' },
  { value: '16:9', label: '16:9 Landscape' },
  { value: '4:3', label: '4:3 Landscape' },
  { value: '3:4', label: '3:4 Portrait' },
  { value: '3:2', label: '3:2 Landscape' },
  { value: '2:3', label: '2:3 Portrait' },
];
const GROK_IMAGE_2_ASPECT_RATIOS = [
  ...GROK_IMAGE_ASPECT_RATIOS,
  { value: '2:1', label: '2:1 Wide' },
  { value: '1:2', label: '1:2 Tall' },
  { value: '19.5:9', label: '19.5:9 Phone Landscape' },
  { value: '9:19.5', label: '9:19.5 Phone Portrait' },
  { value: '20:9', label: '20:9 Ultrawide' },
  { value: '9:20', label: '9:20 Tall Portrait' },
  { value: '21:9', label: '21:9 Cinematic' },
  { value: '5:2', label: '5:2 Panorama' },
];

export const GROK_IMAGE_NODE_DEF: UniversalNodeDefinition = {
  id: 'grok-image',
  type: 'GrokImage',
  title: 'Grok Image',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'image',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model',
      type: 'select',
      options: [
        // Top tier. xAI bills it per (quality × resolution) — see
        // server/config/grokImage.ts — so it exposes the two settings below.
        { value: 'grok-imagine-image-2.0', label: 'Grok Image 2.0' },
        { value: 'grok-image', label: 'Grok Image' },
        { value: 'grok-imagine-image-quality', label: 'Grok Image Quality (Higher fidelity)' },
        { value: 'grok-image-pro', label: 'Grok Image Pro (Legacy alias)' },
      ],
      // grok-image stays the default: the cheapest model (9 cr).
      default: 'grok-image',
    },
    {
      // Grok Image 2.0 only. Never offer xAI's 'auto' — it is billed at
      // whatever quality xAI chooses to serve, after we have reserved.
      id: 'quality',
      label: 'Quality',
      type: 'select',
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
      ],
      default: 'low',
      showWhen: { field: 'selectedModel', value: 'grok-imagine-image-2.0' },
    },
    {
      // Grok Image 2.0 only. xAI's schema: "1k" | "1.5k" | "2k".
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '1k', label: '1K' },
        { value: '1.5k', label: '1.5K' },
        { value: '2k', label: '2K' },
      ],
      default: '1k',
      showWhen: { field: 'selectedModel', value: 'grok-imagine-image-2.0' },
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: GROK_IMAGE_ASPECT_RATIOS,
      // Grok Image 2.0 accepts xAI's full list (verified against the API's own
      // schema: 1:1 3:4 4:3 9:16 16:9 2:3 3:2 9:19.5 19.5:9 9:20 20:9 1:2 2:1
      // 21:9 5:2 auto). 'auto' is left out so the output shape is predictable.
      conditionalOptions: {
        dependsOn: 'selectedModel',
        rules: {
          'grok-imagine-image-2.0': GROK_IMAGE_2_ASPECT_RATIOS,
        },
      },
      default: '1:1',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 4,
      step: 1,
      default: 1,
      infoTooltip: 'Number of images to generate (1-4)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'grok-image-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the image you want Grok to create...',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// ============================================================================
// fal.ai Video Generation Nodes
// ============================================================================

// Kling 2.6 Pro Video Node (fal.ai) - Top-tier cinematic with native audio
// Supports T2V and I2V (1 image)
export const KLING_26_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'kling-26-video',
  type: 'Kling26Video',
  title: 'Kling 2.6',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
      ],
      default: '16:9',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '5', label: '5 seconds' },
        { value: '10', label: '10 seconds' },
      ],
      default: '5',
    },
    {
      id: 'enableAudio',
      label: 'Generate Audio',
      type: 'boolean',
      default: false,
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Kling excels at cinematic motion and dialogue.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Kling 2.5 Turbo Video Node (fal.ai) - Faster, budget-friendly
// Supports T2V and I2V (1 image)
export const KLING_25_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'kling-25-video',
  type: 'Kling25Video',
  title: 'Kling 2.5',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
      ],
      default: '16:9',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '5', label: '5 seconds' },
        { value: '10', label: '10 seconds' },
      ],
      default: '5',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Kling 2.5 Turbo for faster generation.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Unified Kling Video Node (fal.ai) - Single node covering all Kuaishou Kling
// product variants: 2.5 Turbo, 2.6 Pro, and 3.0 Standard/Pro. (Kling Avatar
// remains a separate node since it has a different lipsync I/O contract.)
// Supports T2V, I2V (1 image), and FFLF (2 images, 3.0 only). The mode selector
// drives input handle count via getInputHandlesForNodeType('klingVideo').
export const KLING_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'kling-video',
  type: 'KlingVideo',
  title: 'Kling',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 2,  // Up to 2 images for first-last-frame mode (3.0 only)
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Kling Model',
      type: 'model',
      models: [
        { id: 'kling-2.5-turbo', name: 'Kling 2.5 Turbo', description: 'Fast, budget-friendly' },
        { id: 'kling-2.6', name: 'Kling 2.6 Pro', description: 'Cinematic with native audio' },
        { id: 'kling-3.0-standard', name: 'Kling 3.0 Standard', description: 'Latest gen — standard quality' },
        { id: 'kling-3.0-pro', name: 'Kling 3.0 Pro', description: 'Latest gen — highest quality' },
      ],
      default: 'kling-3.0-standard',
    },
    {
      id: 'mode',
      label: 'Generation Mode',
      type: 'select',
      options: [
        { value: 'text-to-video', label: 'Text-to-Video' },
        { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
        { value: 'first-last-frame', label: 'First-to-Last Frame (2 images, 3.0 only)' },
      ],
      default: 'image-to-video',
      // "3.0 only" is enforced, not just advertised. Only Kling 3.0 has a
      // first-last-frame path server-side — server/routes/falai.ts branches on
      // `kling-3.0`/`kling-o3` twice (endpoint override and the
      // image_url + end_image_url input shape). On 2.5 Turbo / 2.6 the request
      // falls through to the plain TEXT-to-video endpoint carrying VEO-shaped
      // first_frame_url/last_frame_url params and a forced 8s duration — it
      // fails after credits are already reserved. Narrow the option list so the
      // mode cannot be picked there (UniversalNode reconciles saved flows that
      // already sit on the illegal combination).
      conditionalOptions: {
        dependsOn: 'selectedModel',
        rules: {
          'kling-2.5-turbo': [
            { value: 'text-to-video', label: 'Text-to-Video' },
            { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
          ],
          'kling-2.6': [
            { value: 'text-to-video', label: 'Text-to-Video' },
            { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
          ],
        },
      },
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
      ],
      default: '16:9',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '5', label: '5 seconds' },
        { value: '10', label: '10 seconds' },
      ],
      default: '5',
      // Kling 3.0 supports additional durations (3 and 15s) per fal.ai docs;
      // expose them when a 3.0 model is selected.
      conditionalOptions: {
        dependsOn: 'selectedModel',
        rules: {
          'kling-3.0-standard': [
            { value: '3', label: '3 seconds' },
            { value: '5', label: '5 seconds' },
            { value: '10', label: '10 seconds' },
            { value: '15', label: '15 seconds' },
          ],
          'kling-3.0-pro': [
            { value: '3', label: '3 seconds' },
            { value: '5', label: '5 seconds' },
            { value: '10', label: '10 seconds' },
            { value: '15', label: '15 seconds' },
          ],
        },
      },
    },
    {
      id: 'enableAudio',
      label: 'Generate Audio',
      type: 'boolean',
      default: false,
      // 2.5 Turbo does not support audio generation; hide the toggle there.
      showWhen: { field: 'selectedModel', value: 'kling-2.5-turbo', operator: '!=' },
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Kling excels at cinematic motion, dialogue, and realistic scenes.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Kling O3 Video Node (fal.ai) - Latest Kling 3.0 with Standard/Pro quality tiers
// Supports T2V, I2V (1 image), and FFLF (2 images), duration 5-10s, native audio
// DEPRECATED: Use KlingVideo (unified). Kept hidden so existing flows still render.
export const KLING_O3_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'kling-o3-video',
  type: 'KlingO3Video',
  title: 'Kling 3.0',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 2,  // Up to 2 images for first-last-frame mode
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Generation Mode',
      type: 'select',
      options: [
        { value: 'text-to-video', label: 'Text-to-Video' },
        { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
        { value: 'first-last-frame', label: 'First-to-Last Frame (2 images)' },
      ],
      default: 'image-to-video',
    },
    {
      id: 'quality',
      label: 'Quality',
      type: 'select',
      options: [
        { value: 'standard', label: 'Standard' },
        { value: 'pro', label: 'Pro' },
      ],
      default: 'standard',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
      ],
      default: '16:9',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '3', label: '3 seconds' },
        { value: '5', label: '5 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '15', label: '15 seconds' },
      ],
      default: '3',
    },
    {
      id: 'enableAudio',
      label: 'Generate Audio',
      type: 'boolean',
      default: false,
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Kling 3.0 excels at cinematic motion, dialogue, and realistic scenes.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Wan 2.2 Video Node (fal.ai) - Legacy budget option (superseded by Wan 2.6/2.7)
// Supports T2V and I2V (1 image)
// DEPRECATED: Wan 2.6/2.7 (Wan26Video node) is now flagship; this node stays
// hidden in the palette but remains routable so existing flows keep working.
export const WAN_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'wan-video',
  type: 'WanVideo',
  title: 'Wan 2.2 (Legacy)',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
      ],
      default: '16:9',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '480p', label: '480p' },
        { value: '720p', label: '720p' },
      ],
      default: '720p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '3', label: '3 seconds' },
        { value: '5', label: '5 seconds' },
      ],
      default: '5',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Great for complex motion and expressions.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Wan Video Node (fal.ai) - Multi-version: Wan 2.6 (legacy) + Wan 2.7 (default).
// Supports T2V and I2V (1 image), 720p/1080p, 5/10/15 sec, multi-shot scene segmentation.
// Wan 2.7 added Thinking Mode, native 1080p, instruction-based editing.
// Type stays 'Wan26Video' for backwards-compat with existing saved flows.
export const WAN26_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'wan26-video',
  type: 'Wan26Video',
  title: 'Wan',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'model',
      models: [
        { id: 'wan-2.7', name: 'Wan 2.7', description: 'Latest. Thinking Mode, native 1080p, instruction-based editing.' },
        { id: 'wan-2.6', name: 'Wan 2.6', description: 'Multi-shot scene segmentation, character consistency.' },
      ],
      default: 'wan-2.7',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
        { value: '4:3', label: '4:3 Classic' },
        { value: '3:4', label: '3:4 Portrait Classic' },
      ],
      default: '16:9',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '720p', label: '720p' },
        { value: '1080p', label: '1080p' },
      ],
      default: '720p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '5', label: '5 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '15', label: '15 seconds' },
      ],
      default: '5',
    },
    {
      id: 'multiShots',
      label: 'Multi-Shot Mode',
      type: 'select',
      options: [
        { value: 'true', label: 'Enabled (Scene segmentation)' },
        { value: 'false', label: 'Disabled (Single shot)' },
      ],
      default: 'true',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Use [0-3s] Shot 1. [3-6s] Shot 2. format for multi-shot videos.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Wan Animate Video Node (fal.ai) - Character animation from video motion
// Supports Replace (swap characters in video) and Move (replicate expressions/movements)
// Input: video_url (motion reference) + image_url (character to animate)
export const WAN_ANIMATE_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'wan-animate-video',
  type: 'WanAnimateVideo',
  title: 'Wan Animate',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['video', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 2, // Video + Image
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Animation Mode',
      type: 'select',
      options: [
        { value: 'replace', label: 'Replace - Swap character in video' },
        { value: 'move', label: 'Move - Replicate expressions/movements' },
      ],
      default: 'replace',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '480p', label: '480p' },
        { value: '580p', label: '580p' },
        { value: '720p', label: '720p' },
      ],
      default: '480p',
    },
    {
      id: 'useTurbo',
      label: 'Turbo Mode',
      type: 'select',
      options: [
        { value: 'true', label: 'Enabled (Faster + better quality)' },
        { value: 'false', label: 'Disabled' },
      ],
      default: 'true',
    },
    {
      id: 'numInferenceSteps',
      label: 'Inference Steps',
      type: 'slider',
      min: 4,
      max: 40,
      step: 1,
      default: 6,
      description: 'More steps = better quality, slower. 6 optimal for Turbo, 20 for non-Turbo.',
    },
    {
      id: 'guidanceScale',
      label: 'Guidance Scale',
      type: 'slider',
      min: 0.5,
      max: 3,
      step: 0.1,
      default: 1,
      description: 'How strongly to follow input. 1.0 = balanced, higher = stricter.',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'animate-info-text',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: '**Instructions:** Connect a VIDEO (motion) + IMAGE (character).\n\n**Turbo:** Fast + quality optimized at 6 steps.\n**Steps:** More = better quality, slower. 6 for Turbo, 20 for non-Turbo.\n**Guidance:** 1.0 balanced, higher = stricter adherence.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: false,
        minHeight: '80px',
        maxHeight: '120px',
        value: '',
        onChange: () => {},
        disabled: true,
        readOnly: true,
      },
    },
  ],
};

// LTX Video Node (fal.ai) - LTX-2 and LTX-2.3 families
// Supports T2V and I2V (1 image), audio generation
//
// PRICING NOTE (LTX-2.3 Pro): fal.ai bills T2V 33% higher than I2V:
//   T2V: $0.08/s @1080p, $0.16/s @1440p, $0.32/s @2160p  (37/74/148 cr/s)
//   I2V: $0.06/s @1080p, $0.12/s @1440p, $0.24/s @2160p  (28/56/112 cr/s)
//   Fast: $0.04/s @1080p (T2V only, 19 cr/s)
// Mode is inferred at submit time from whether an `image_url` input is connected:
//   no image → text-to-video → ltx-2.3-t2v SKU (charged at T2V rate)
//   image    → image-to-video → ltx-2.3 SKU   (charged at I2V rate)
// See server/routes/falai.ts (creditModelId remap) and
// src/utils/creditCostUtils.ts → calculateLTX23CreditCost().
export const LTX_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'ltx-video',
  type: 'LTXVideo',
  title: 'LTX',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'model',
      models: [
        { id: 'ltx-2.3', name: 'LTX-2.3 Pro', description: 'Latest, highest quality with audio' },
        { id: 'ltx-2.3-fast', name: 'LTX-2.3 Fast', description: 'Faster, up to 20s, with audio' },
        // 'ltx-2' and 'ltx-2-fast' were REMOVED from this dropdown (migration
        // 129). fal deprecates the LTX-2 endpoints on 2026-08-15 and names
        // LTX-2.3 as the migration target, so new work started on them breaks;
        // and 'ltx-2-fast' bills a flat 14 cr/s against a per-megapixel price
        // that costs us $0.0737/s at 1440p and $0.1659/s at 2160p — it LOSES
        // money at every tier above 1080p.
        //
        // NOT aliased and NOT retired from pricing. Their MODEL_BASE_COSTS
        // entries, the CreditService fallback, their falModels.ts slugs and
        // their model_registry rows are all left intact, so saved flows that
        // pin them keep pricing and running until fal switches them off. Same
        // retire-from-the-dropdown-keep-the-cost-row shape as
        // gemini-omni-flash-preview above and the retired Ollama text ids
        // (migration 122). Aliasing them onto LTX-2.3 would change what an
        // already-saved flow bills, which is the trap migration 122's header
        // spells out for gemini-3.1-flash-lite.
        //
        // Their `duration` conditionalOptions rules below are deliberately
        // kept: a saved flow still renders this node and must still show the
        // 6/8/10 options its pinned model accepts.
      ],
      default: 'ltx-2.3-fast',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
      ],
      default: '16:9',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      // fal's enum for every LTX-2.3 endpoint is ["1080p","1440p","2160p"] —
      // lowercase p, unlike MiniMax H3's capital P. 2160p was missing here
      // while the route already had a branch for it on the LTX-2 models.
      //
      // Each tier bills its own rate on the Fast model (28 / 56 / 111 cr/s,
      // migration 128), so picking one is a real price decision. NOTE the
      // interaction with duration: fal only allows durations over 10 seconds at
      // 1080p, so a 1440p or 2160p selection is DOWNGRADED to 1080p above 10s
      // — and billed at 1080p, not at what was picked. The duration options
      // below label that ("12 seconds (1080p only)").
      //
      // Default stays 1080p: fal's own default, and what every saved flow has.
      options: [
        { value: '1080p', label: '1080p' },
        { value: '1440p', label: '1440p' },
        { value: '2160p', label: '2160p (4K)' },
      ],
      default: '1080p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '6', label: '6 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '12', label: '12 seconds' },
        { value: '14', label: '14 seconds' },
        { value: '16', label: '16 seconds' },
        { value: '18', label: '18 seconds' },
        { value: '20', label: '20 seconds' },
      ],
      default: '6',
      conditionalOptions: {
        dependsOn: 'selectedModel',
        rules: {
          'ltx-2': [
            { value: '6', label: '6 seconds' },
            { value: '8', label: '8 seconds' },
            { value: '10', label: '10 seconds' },
          ],
          'ltx-2-fast': [
            { value: '6', label: '6 seconds' },
            { value: '8', label: '8 seconds' },
            { value: '10', label: '10 seconds' },
          ],
          'ltx-2.3': [
            { value: '6', label: '6 seconds' },
            { value: '8', label: '8 seconds' },
            { value: '10', label: '10 seconds' },
          ],
          'ltx-2.3-fast': [
            { value: '6', label: '6 seconds' },
            { value: '8', label: '8 seconds' },
            { value: '10', label: '10 seconds' },
            { value: '12', label: '12 seconds (1080p only)' },
            { value: '14', label: '14 seconds (1080p only)' },
            { value: '16', label: '16 seconds (1080p only)' },
            { value: '18', label: '18 seconds (1080p only)' },
            { value: '20', label: '20 seconds (1080p only)' },
          ],
        },
      },
    },
    {
      id: 'enableAudio',
      label: 'Generate Audio',
      type: 'boolean',
      default: false,
      infoTooltip: 'Generate native audio alongside video',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... LTX creates cinematic content with native audio.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// LTX-2 Fast Video Node (fal.ai) - Faster inference, T2V only
// Supports T2V only (no image input)
export const LTX_FAST_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'ltx-fast-video',
  type: 'LTXFastVideo',
  title: 'LTX Fast',
  category: 'processor',
  variant: 'purple',
  hidden: true, // Hidden - merged into LTX-2
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
      ],
      default: '16:9',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '1080p', label: '1080p' },
        { value: '1440p', label: '1440p' },
      ],
      default: '1080p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '6', label: '6 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '10', label: '10 seconds' },
      ],
      default: '6',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Fast generation with good quality.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// PixVerse Video Node (fal.ai) - Stylized + lifelike content
// v6 (default, Nov 2025): lifelike physics + striking visuals; supports I2V.
// v5.5: original stylized model, still supported for existing flows.
// acceptsMediaTypes is now ['text', 'image'] since v6 supports I2V.
export const PIXVERSE_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'pixverse-video',
  type: 'PixVerseVideo',
  title: 'PixVerse',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'PixVerse Version',
      type: 'model',
      models: [
        { id: 'pixverse-6', name: 'PixVerse v6', description: 'Latest (Nov 2025). Lifelike physics and striking visuals.' },
        { id: 'pixverse-5.5', name: 'PixVerse v5.5', description: 'Original stylized model.' },
      ],
      default: 'pixverse-6',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
      ],
      default: '16:9',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '720p', label: '720p' },
        { value: '1080p', label: '1080p' },
      ],
      default: '720p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '5', label: '5 seconds' },
        { value: '8', label: '8 seconds' },
      ],
      default: '5',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Great for stylized and artistic content.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Hunyuan 1.5 Video Node (fal.ai) - Tencent's best, T2V only
// Supports T2V only (no image input)
//
// NO Duration setting, deliberately. Hunyuan 1.5's fal schema has no `duration`
// field at all: length is `num_frames`, whose default and maximum are both 121
// (~5 seconds), and `resolution` is a const "480p". The 3s/5s select this node
// used to carry was inert — the output was always ~5s whichever the user picked
// — while credits were reserved against the number they picked, so choosing 3s
// billed 105 credits for a clip that costs us ~113. Cost is now pinned to the
// fixed 5s on both sides (see HUNYUAN_15_FIXED_SECONDS). Do not re-add a
// Duration control unless fal adds a real length parameter.
export const HUNYUAN_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'hunyuan-video',
  type: 'HunyuanVideo',
  title: 'Hunyuan',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: "Describe your video... Tencent's latest video model.",
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// MiniMax Hailuo Video Node (fal.ai) - Natural motion
// Supports T2V and I2V (1 image). Defaults to Hailuo 2.3 (Oct 2025) which
// adds improved micro-expressions and physical actions over Hailuo 02.
export const MINIMAX_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'minimax-video',
  type: 'MinimaxVideo',
  title: 'MiniMax',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Hailuo Version',
      type: 'model',
      models: [
        { id: 'hailuo-2.3', name: 'Hailuo 2.3', description: 'Latest (Oct 2025). Improved micro-expressions and physical actions.' },
        { id: 'minimax-hailuo', name: 'Hailuo 02 Standard', description: 'Original — natural motion synthesis.' },
      ],
      default: 'hailuo-2.3',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '768p', label: '768p' },
        { value: '1080p', label: '1080p' },
      ],
      default: '768p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '4', label: '4 seconds' },
        { value: '6', label: '6 seconds' },
      ],
      default: '6',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Excellent for natural motion synthesis.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Kling Avatar Video Node (fal.ai) - Lip sync from image
// Requires 1 image AND 1 audio track (no text-to-video mode at all — the server
// rejects a run that is missing either, before reserving credits).
//
// NO Duration setting, deliberately. Kling AI Avatar v2's schema has no
// `duration` field: the clip is exactly as long as the connected audio. A
// control that cannot move the output but does move the bill is worse than no
// control — the 5s/10s select this node used to carry under-billed a 30s track
// by ~4x. The billed seconds are now MEASURED from the connected audio
// (probeAudioDuration → node.data.connectedAudioDuration) and the node refuses
// to run when they cannot be measured. Do not re-add a Duration control unless
// fal adds a real length parameter.
export const KLING_AVATAR_NODE_DEF: UniversalNodeDefinition = {
  id: 'kling-avatar',
  type: 'KlingAvatar',
  title: 'Kling Avatar',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['image', 'audio'],
  outputsMediaType: 'video',
  maxInputConnections: 2,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe speech/dialogue for lip sync... Connect a portrait image AND an audio node.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Veo Video Node - Google's Veo 3.1 (routes Google-direct)
// Supports T2V, I2V (single image), and FLF (first & last frame interpolation)
export const VEO_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'veo-video',
  type: 'VeoVideo',
  title: 'Veo 3.1',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 2,  // Allow up to 2 images for first & last frame mode
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Generation Mode',
      type: 'select',
      options: [
        { value: 'text-to-video', label: 'Text-to-Video' },
        { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
        { value: 'first-last-frame', label: 'First-to-Last Frame (2 images)' },
      ],
      default: 'image-to-video',
      // Google: Veo 3.1 Lite has no lastFrame interpolation (T2V / I2V only).
      conditionalOptions: {
        dependsOn: 'selectedModel',
        rules: {
          'veo-3.1-lite-generate-preview': [
            { value: 'text-to-video', label: 'Text-to-Video' },
            { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
          ],
        },
      },
    },
    {
      id: 'selectedModel',
      label: 'VEO Model',
      type: 'model',
      models: [
        { id: 'veo-3.1-lite-generate-preview', name: 'VEO 3.1 Lite', description: 'Cheapest, 720p/1080p only' },
        { id: 'veo-3.1-fast-generate-preview', name: 'VEO 3.1 Fast', description: 'Fastest generation' },
        { id: 'veo-3.1-generate-preview', name: 'VEO 3.1 Standard', description: 'Higher quality' },
      ],
      default: 'veo-3.1-fast-generate-preview',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '720p', label: '720p (Base)' },
        { value: '1080p', label: '1080p (8s only)' },
        { value: '4k', label: '4K (8s only, Fast/Standard)' },
      ],
      default: '720p',
      // Lite tier does not support 4K — restrict per-model.
      conditionalOptions: {
        dependsOn: 'selectedModel',
        rules: {
          'veo-3.1-lite-generate-preview': [
            { value: '720p', label: '720p (Base)' },
            { value: '1080p', label: '1080p (8s only)' },
          ],
        },
      },
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
      ],
      default: '16:9',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '4', label: '4 seconds' },
        { value: '6', label: '6 seconds' },
        { value: '8', label: '8 seconds' },
      ],
      default: '4',
      description: 'Note: First-to-Last Frame mode requires 8 seconds.',
      // Conditional options based on mode
      conditionalOptions: {
        dependsOn: 'mode',
        rules: {
          'first-last-frame': [
            { value: '8', label: '8 seconds (required for this mode)' },
          ],
        },
      },
    },
    {
      // Maps to the @google/genai GenerateVideosConfig.generateAudio boolean
      // ("Whether to generate audio along with the video"). Default true keeps
      // VEO 3.1's historical always-on audio behavior.
      id: 'enableAudio',
      label: 'Generate Audio',
      type: 'boolean',
      default: true,
      infoTooltip: 'VEO 3.1 generates native audio. Turn off for a silent video.',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: "Describe your video... Google VEO 3.1 with audio.",
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Gemini Omni Video Node (Google direct, Gemini API — Interactions endpoint)
// Live model: Gemini Omni 1.1 Flash — API id `gemini-omni-1.1-flash`.
// Supports T2V and I2V (single image) ONLY here. Google's docs state video
// interpolation is not supported on the preview, so first-last-frame (FFLF) is
// NOT offered; 1.1 adds keyframes but we do not expose them (see below).
// NOTE: V2V ("conversational editing") is NOT built — the API does not yet
// process video references. Keep acceptsMediaTypes ['text','image'].
// Specs: 720p @ 24fps, 3–10s, $0.10/sec of 720p output (same headline rate as
// VEO 3.1 Fast). Audio output is NOT reliably available in the API → no audio UI.
//
// DELIBERATELY NOT EXPOSED on 1.1, despite the model supporting them: 1080p/4K,
// scene extension to 40s, start/end keyframes, video-as-style-reference, and the
// 360p draft mode. Each needs its own verified price (1080p/4K in particular
// would engage the 1.5x/3x resolution ladder, and Google's per-second rate above
// 720p is not the ladder's assumption — that is exactly the mis-charge migration
// 123 had to undo for veo fast). This change is a SHUTDOWN-CONTINUITY fix, not a
// feature expansion; the node keeps the preview's exact surface so the only
// behavioural difference is which model id runs. Expose them in a later change
// with their own pricing review.
//
// Routes through VeoVideoService, which intercepts any `gemini-omni*` id and
// posts to the dedicated /api/gemini-omni/create route (Google's SYNCHRONOUS
// Interactions API) rather than /api/veo — Omni is not available on
// generateVideos(). Cloned from VEO_VIDEO_NODE_DEF.
export const GEMINI_OMNI_NODE_DEF: UniversalNodeDefinition = {
  id: 'gemini-omni',
  type: 'GeminiOmni',
  title: 'Gemini Omni',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 2,  // Allow up to 2 images for first & last frame mode
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Generation Mode',
      type: 'select',
      // NOTE: No 'first-last-frame' option — Google's docs state Gemini Omni does
      // NOT support video interpolation ("Video extension and video interpolation
      // ... are not supported"), so FFLF is intentionally omitted.
      options: [
        { value: 'text-to-video', label: 'Text-to-Video' },
        { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
      ],
      default: 'image-to-video',
    },
    {
      id: 'selectedModel',
      label: 'Model',
      type: 'model',
      // `gemini-omni-flash-preview` was REMOVED from this dropdown on 2026-09-01.
      // Google shuts it down on 2026-09-30, so a user starting new work on it
      // today builds a flow that breaks inside a month. It remains callable —
      // the server allowlist in routes/geminiOmni.ts still accepts it and its
      // model_registry row and cost-table entries are all still live — so saved
      // flows that pin it keep running and pricing until the shutdown date.
      // Same retire-from-the-dropdown-keep-the-cost-row shape as the retired
      // Ollama text ids (migration 122) and the Gemini text ids kept in the
      // routes/googleai.ts allowlist.
      models: [
        { id: 'gemini-omni-1.1-flash', name: 'Gemini Omni 1.1 Flash', description: '720p, T2V + I2V' },
      ],
      default: 'gemini-omni-1.1-flash',
    },
    {
      // Gemini Omni is 720p-only — do NOT expose 1080p/4K. The single fixed
      // option keeps the credit resolution multiplier at 1× (720p base).
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '720p', label: '720p (Base)' },
      ],
      default: '720p',
    },
    {
      // UNCERTAIN: Gemini Omni's supported aspect ratios are not documented.
      // Defaulting to 16:9 plus the same set VEO offers (16:9 / 9:16).
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
      ],
      default: '16:9',
    },
    {
      // Duration as bare-string seconds (NOT '4s') — server parses to number.
      // Gemini Omni supports 3–10s. First-to-Last Frame forces 8s (same as VEO).
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '3', label: '3 seconds' },
        { value: '4', label: '4 seconds' },
        { value: '5', label: '5 seconds' },
        { value: '6', label: '6 seconds' },
        { value: '7', label: '7 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '9', label: '9 seconds' },
        { value: '10', label: '10 seconds' },
      ],
      default: '5',
      description: 'Note: First-to-Last Frame mode requires 8 seconds.',
      conditionalOptions: {
        dependsOn: 'mode',
        rules: {
          'first-last-frame': [
            { value: '8', label: '8 seconds (required for this mode)' },
          ],
        },
      },
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: "Describe your video... Google Gemini Omni (720p).",
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Grok Video Node (xAI via fal.ai) - Video generation with native audio
// Supports text-to-video and image-to-video
export const GROK_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'grok-video',
  type: 'GrokVideo',
  title: 'Grok Video',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model',
      type: 'select',
      options: [
        { value: 'grok-imagine-video', label: 'Grok Video (Standard)' },
        // The VALUE stays on the -preview id on purpose. xAI keeps it as an
        // alias of grok-imagine-video-1.5, and every saved flow, the pricing
        // key in CreditService and the model_registry row are all keyed to it.
        // Renaming the value buys nothing and risks mispricing; only the label
        // was wrong, since the model left preview.
        { value: 'grok-imagine-video-1.5-preview', label: 'Grok Video 1.5 (Top I2V)' },
      ],
      default: 'grok-imagine-video',
      infoTooltip: 'Grok Video 1.5 (GA August 2026) leads the Image-to-Video Arena leaderboard.',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
      ],
      default: '16:9',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '4', label: '4 seconds' },
        { value: '5', label: '5 seconds' },
        { value: '6', label: '6 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '12', label: '12 seconds' },
        { value: '15', label: '15 seconds' },
      ],
      default: '6',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '720p', label: '720p' },
        { value: '480p', label: '480p' },
      ],
      default: '480p',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'grok-video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Grok generates video with native audio.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Seedance 2.0 Video Node (ByteDance via fal.ai)
//
// Migrated off PiAPI in migration 113. The three fal tiers are standard (which
// this node has always labelled "Pro"), fast, and mini. The model IDs
// 'seedance-2' and 'seedance-2-fast' are UNCHANGED from the PiAPI era so saved
// flows keep resolving; only the provider and endpoint moved underneath them.
//
// Deliberately not exposed:
//   • 1080p / 4K — available on the standard tier only. Exposing a resolution
//     that exists on one of three tiers needs conditionalOptions, and switching
//     tiers afterwards would leave a stale value that the mini endpoint rejects
//     (its enum is 480p/720p). 4K also bills at ~718 cr/s, which would make a
//     15s run the most expensive generation in the product by 2x.
//   • bitrate_mode — absent from the mini schema, same stale-value problem, and
//     it is a pure encode-quality knob with no effect on what we store.
//   • reference-to-video — needs multi-reference input UI we do not have.
export const SEEDANCE2_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'seedance2-video',
  type: 'Seedance2Video',
  title: 'Seedance 2',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 2,  // Up to 2 images for first-last-frame mode
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model Version',
      type: 'model',
      models: [
        { id: 'seedance-2-fast', name: 'Seedance 2 Fast', description: 'Faster generation, lower cost.' },
        { id: 'seedance-2', name: 'Seedance 2 Pro', description: 'Higher quality, more detail.' },
        { id: 'seedance-2-mini', name: 'Seedance 2 Mini', description: 'Cheapest tier — half the price of Pro.' },
      ],
      default: 'seedance-2-fast',
    },
    {
      id: 'mode',
      label: 'Generation Mode',
      type: 'select',
      options: [
        { value: 'text-to-video', label: 'Text-to-Video' },
        { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
        { value: 'first-last-frame', label: 'First-to-Last Frame (2 images)' },
      ],
      // 'image-to-video' reproduces the pre-migration behaviour for saved flows
      // that have no `mode` key: with an image connected the server picks the
      // i2v endpoint, and with none it falls through to text-to-video.
      default: 'image-to-video',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
        { value: '21:9', label: '21:9 Cinematic' },
      ],
      default: '16:9',
      // fal defaults i2v aspect_ratio to "auto" (infer from the input image),
      // which is what you want with a source frame — and 2.5 hard-requires it.
      showWhen: { field: 'mode', value: 'text-to-video', operator: '=' },
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '480p', label: '480p' },
        { value: '720p', label: '720p' },
      ],
      default: '720p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '4', label: '4 seconds' },
        { value: '5', label: '5 seconds' },
        { value: '6', label: '6 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '12', label: '12 seconds' },
        { value: '15', label: '15 seconds' },
      ],
      default: '6',
    },
    {
      id: 'enableAudio',
      label: 'Generate Audio',
      type: 'boolean',
      default: true,
      infoTooltip: 'Native audio. fal charges the same with or without it, so this is free.',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'seedance2-video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Seedance 2 supports text-to-video, image-to-video and first-to-last-frame.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Seedance 2.5 Video Node (ByteDance via fal.ai)
//
// Endpoints live in the BARE `bytedance/` namespace — see server/config/falModels.ts.
// Schema constraints that shape this definition (fal OpenAPI, verified Aug 2026):
//   • `duration` is a STRING enum, so every option value here is a string.
//     fal accepts "4".."30" but we cap the UI at 15s to match every other video
//     node — a 30s/720p run would cost 6570 credits (~$21.88), by a wide margin
//     the most expensive single generation in the product. "auto" is
//     deliberately NOT exposed: it makes the credit estimate unknowable.
//   • `resolution` is 480p/720p ONLY. 2.5 dropped 2.0's 1080p and 4K.
//   • `aspect_ratio` is text-to-video only; on image-to-video fal documents it
//     as "Always auto", so the control is hidden and the server never sends it.
//   • Audio is free — fal states the cost is identical with or without it — so
//     there is no `-audio` SKU and no surcharge. Default on.
//   • There is no `seed` input on any 2.5 endpoint.
export const SEEDANCE_25_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'seedance25-video',
  type: 'Seedance25Video',
  title: 'Seedance 2.5',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'video',
  maxInputConnections: 2,  // Up to 2 images for first-last-frame mode
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Generation Mode',
      type: 'select',
      options: [
        { value: 'text-to-video', label: 'Text-to-Video' },
        { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
        { value: 'first-last-frame', label: 'First-to-Last Frame (2 images)' },
      ],
      default: 'image-to-video',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
        { value: '4:3', label: '4:3 Landscape' },
        { value: '3:4', label: '3:4 Portrait' },
        { value: '21:9', label: '21:9 Cinematic' },
      ],
      default: '16:9',
      // fal: "Always auto for image-to-video" — the field has no enum there.
      showWhen: { field: 'mode', value: 'text-to-video', operator: '=' },
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: '480p', label: '480p' },
        { value: '720p', label: '720p' },
      ],
      default: '720p',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '4', label: '4 seconds' },
        { value: '5', label: '5 seconds' },
        { value: '6', label: '6 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '12', label: '12 seconds' },
        { value: '15', label: '15 seconds' },
      ],
      default: '5',
    },
    {
      id: 'enableAudio',
      label: 'Generate Audio',
      type: 'boolean',
      default: true,
      infoTooltip: 'Native audio. fal charges the same with or without it, so this is free.',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'seedance25-video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... Seedance 2.5 handles multi-shot storytelling, real-world physics and native audio.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// ============================================================================
// HEYGEN NODES (via fal.ai)
// ============================================================================

const HEYGEN_AVATAR_OPTIONS = [
  { value: 'Adriana SuitSofa Front', label: 'Adriana - Business' },
  { value: 'Ann Therapist', label: 'Ann - Therapist' },
  { value: 'Annie Office Standing Front', label: 'Annie - Office' },
  { value: 'Bahar Suit Front', label: 'Bahar - Suit' },
  { value: 'Connie Business Front', label: 'Connie - Business' },
  { value: 'Daisy in Suit', label: 'Daisy - Suit' },
  { value: 'Elenora Suit Front', label: 'Elenora - Elegant' },
  { value: 'Brandon Business Standing Front', label: 'Brandon - Business' },
  { value: 'Bryan Suit Front', label: 'Bryan - Dynamic' },
  { value: 'Colin Suit Front', label: 'Colin - Formal' },
  { value: 'Crisanto Suit Front', label: 'Crisanto - Professional' },
  { value: 'Dexter Suit Front', label: 'Dexter - Professional' },
  { value: 'Edward', label: 'Edward' },
  { value: 'August Employee Front', label: 'August - Employee' },
  { value: 'Diran Suit Front', label: 'Diran - Suit' },
];

const HEYGEN_VOICE_OPTIONS = [
  { value: 'Warm Pro Narrator', label: 'Warm Pro Narrator' },
  { value: 'John Doe', label: 'John Doe' },
  { value: 'Ivy', label: 'Ivy' },
  { value: 'Brittney', label: 'Brittney' },
  { value: 'Jessica Anne Bogart', label: 'Jessica Anne' },
  { value: 'Michael C', label: 'Michael C' },
  { value: 'Andrew', label: 'Andrew' },
  { value: 'Grace Elder', label: 'Grace Elder' },
  { value: 'Helen', label: 'Helen' },
  { value: 'Mellow Marcus', label: 'Mellow Marcus' },
  { value: 'Jenny', label: 'Jenny' },
  { value: 'Aaron', label: 'Aaron' },
  { value: 'Willow', label: 'Willow' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Patrick', label: 'Patrick' },
];

const HEYGEN_DURATION_OPTIONS = [
  { value: '15', label: '~15 seconds' },
  { value: '30', label: '~30 seconds' },
  { value: '60', label: '~60 seconds' },
  { value: '90', label: '~90 seconds' },
];

const HEYGEN_RESOLUTION_OPTIONS = [
  { value: '720p', label: '720p' },
  { value: '480p', label: '480p' },
  { value: '1080p', label: '1080p' },
];

const HEYGEN_LANGUAGE_OPTIONS = [
  { value: 'English', label: 'English' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'French', label: 'French' },
  { value: 'Portuguese', label: 'Portuguese' },
  { value: 'German', label: 'German' },
  { value: 'Italian', label: 'Italian' },
  { value: 'Japanese', label: 'Japanese' },
  { value: 'Korean', label: 'Korean' },
  { value: 'Chinese', label: 'Chinese (Mandarin)' },
  { value: 'Arabic', label: 'Arabic' },
  { value: 'Hindi', label: 'Hindi' },
  { value: 'Dutch', label: 'Dutch' },
  { value: 'Polish', label: 'Polish' },
  { value: 'Russian', label: 'Russian' },
  { value: 'Turkish', label: 'Turkish' },
  { value: 'Indonesian', label: 'Indonesian' },
  { value: 'Swedish', label: 'Swedish' },
  { value: 'Ukrainian', label: 'Ukrainian' },
  { value: 'Vietnamese', label: 'Vietnamese' },
  { value: 'Greek', label: 'Greek' },
];

// HeyGen Avatar 3 (Engine III) — $0.0167/sec
export const HEYGEN_AVATAR_V3_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-avatar-v3',
  type: 'HeyGenAvatarV3',
  title: 'HeyGen Avatar 3',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'video',
  hasInputHandle: false,
  hasOutputHandle: true,
  settings: [
    { id: 'avatar', label: 'Avatar', type: 'select', options: HEYGEN_AVATAR_OPTIONS, default: 'Adriana SuitSofa Front' },
    { id: 'voice', label: 'Voice', type: 'select', options: HEYGEN_VOICE_OPTIONS, default: 'Warm Pro Narrator' },
    {
      id: 'duration',
      label: 'Script Duration (estimate)',
      type: 'select',
      options: HEYGEN_DURATION_OPTIONS,
      default: '30',
      infoTooltip: 'Used for credit estimation only. Actual duration is determined by your script length (~2–3 words per second).',
    },
    { id: 'resolution', label: 'Resolution', type: 'select', options: HEYGEN_RESOLUTION_OPTIONS, default: '720p' },
  ],
  components: [
    {
      id: 'heygen-v3-script',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter the script your avatar will speak...\n(~2–3 words per second)',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// HeyGen Avatar 4 (Engine IV) — $0.1000/sec, premium lip-sync
export const HEYGEN_AVATAR_V4_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-avatar-v4',
  type: 'HeyGenAvatarV4',
  title: 'HeyGen Avatar 4',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'video',
  hasInputHandle: false,
  hasOutputHandle: true,
  settings: [
    { id: 'avatar', label: 'Avatar', type: 'select', options: HEYGEN_AVATAR_OPTIONS, default: 'Adriana SuitSofa Front' },
    { id: 'voice', label: 'Voice', type: 'select', options: HEYGEN_VOICE_OPTIONS, default: 'Warm Pro Narrator' },
    {
      id: 'duration',
      label: 'Script Duration (estimate)',
      type: 'select',
      options: HEYGEN_DURATION_OPTIONS,
      default: '30',
      infoTooltip: 'Used for credit estimation only. Actual duration is determined by your script length (~2–3 words per second).',
    },
    { id: 'resolution', label: 'Resolution', type: 'select', options: HEYGEN_RESOLUTION_OPTIONS, default: '720p' },
  ],
  components: [
    {
      id: 'heygen-v4-script',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter the script your avatar will speak...\nHyper-realistic Engine IV lip-sync.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// HeyGen Avatar V (Avatar 5) — $0.10/sec on fal.ai, current flagship digital-twin
// tier with 175+ language phoneme-level lip-sync. Endpoint: fal-ai/heygen/avatar5/digital-twin
export const HEYGEN_AVATAR_V_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-avatar-v',
  type: 'HeyGenAvatarV',
  title: 'HeyGen Avatar V',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'video',
  hasInputHandle: false,
  hasOutputHandle: true,
  settings: [
    { id: 'avatar', label: 'Avatar', type: 'select', options: HEYGEN_AVATAR_OPTIONS, default: 'Adriana SuitSofa Front' },
    { id: 'voice', label: 'Voice', type: 'select', options: HEYGEN_VOICE_OPTIONS, default: 'Warm Pro Narrator' },
    {
      id: 'duration',
      label: 'Script Duration (estimate)',
      type: 'select',
      options: HEYGEN_DURATION_OPTIONS,
      default: '30',
      infoTooltip: 'Used for credit estimation only. Actual duration is determined by your script length (~2–3 words per second).',
    },
    { id: 'resolution', label: 'Resolution', type: 'select', options: HEYGEN_RESOLUTION_OPTIONS, default: '720p' },
  ],
  components: [
    {
      id: 'heygen-v5-script',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter the script your avatar will speak...\nFlagship Avatar V — 175+ language lip-sync.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// HeyGen Video Agent v2 — $0.0333/sec, natural language → full video
export const HEYGEN_VIDEO_AGENT_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-video-agent',
  type: 'HeyGenVideoAgent',
  title: 'HeyGen Video Agent',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'video',
  hasInputHandle: false,
  hasOutputHandle: true,
  settings: [
    {
      id: 'orientation',
      label: 'Orientation',
      type: 'select',
      options: [
        { value: 'landscape', label: 'Landscape' },
        { value: 'portrait', label: 'Portrait' },
      ],
      default: 'landscape',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: '30', label: '30 seconds' },
        { value: '60', label: '60 seconds' },
        { value: '90', label: '90 seconds' },
      ],
      default: '60',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'heygen-agent-prompt',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video in natural language...\nThe AI agent will automatically choose avatars, scenes, and narration.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// HeyGen Translate Precision — $0.1000/sec, high-quality dubbing
export const HEYGEN_TRANSLATE_PRECISION_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-translate-precision',
  type: 'HeyGenTranslatePrecision',
  title: 'HeyGen Translate',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['video'],
  outputsMediaType: 'video',
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    { id: 'outputLanguage', label: 'Target Language', type: 'select', options: HEYGEN_LANGUAGE_OPTIONS, default: 'Spanish' },
    {
      id: 'duration',
      label: 'Video Duration (estimate)',
      type: 'select',
      options: [
        { value: '15', label: '~15 seconds' },
        { value: '30', label: '~30 seconds' },
        { value: '60', label: '~60 seconds' },
        { value: '120', label: '~2 minutes' },
        { value: '180', label: '~3 minutes' },
      ],
      default: '60',
      infoTooltip: 'Estimated duration of the video to translate, used for credit reservation.',
    },
  ],
  components: [
    {
      id: 'heygen-translate-precision-prompt',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Connect a video output node above.\nThe video will be translated and dubbed automatically.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: false,
        minHeight: '60px',
        maxHeight: '120px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// HeyGen Translate Speed — $0.0500/sec, fast dubbing
export const HEYGEN_TRANSLATE_SPEED_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-translate-speed',
  type: 'HeyGenTranslateSpeed',
  title: 'HeyGen Translate Fast',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['video'],
  outputsMediaType: 'video',
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    { id: 'outputLanguage', label: 'Target Language', type: 'select', options: HEYGEN_LANGUAGE_OPTIONS, default: 'Spanish' },
    {
      id: 'duration',
      label: 'Video Duration (estimate)',
      type: 'select',
      options: [
        { value: '15', label: '~15 seconds' },
        { value: '30', label: '~30 seconds' },
        { value: '60', label: '~60 seconds' },
        { value: '120', label: '~2 minutes' },
        { value: '180', label: '~3 minutes' },
      ],
      default: '60',
      infoTooltip: 'Estimated duration of the video to translate, used for credit reservation.',
    },
  ],
  components: [
    {
      id: 'heygen-translate-speed-prompt',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Connect a video output node above.\nFaster translation at lower cost.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: false,
        minHeight: '60px',
        maxHeight: '120px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// HeyGen Photo Video (Image-to-Video via Avatar 4) — $0.1000/sec
export const HEYGEN_PHOTO_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-photo-video',
  type: 'HeyGenPhotoVideo',
  title: 'HeyGen Photo Video',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['image'],
  outputsMediaType: 'video',
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    { id: 'voice', label: 'Voice', type: 'select', options: HEYGEN_VOICE_OPTIONS, default: 'Warm Pro Narrator' },
    {
      id: 'talkingStyle',
      label: 'Talking Style',
      type: 'select',
      options: [
        { value: 'stable', label: 'Stable' },
        { value: 'expressive', label: 'Expressive' },
      ],
      default: 'stable',
    },
    {
      id: 'duration',
      label: 'Script Duration (estimate)',
      type: 'select',
      options: HEYGEN_DURATION_OPTIONS,
      default: '30',
      infoTooltip: 'Used for credit estimation only. Actual duration is determined by your script length (~2–3 words per second).',
    },
    { id: 'resolution', label: 'Resolution', type: 'select', options: HEYGEN_RESOLUTION_OPTIONS, default: '720p' },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'heygen-photo-script',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter the script the person in your photo will speak...\nConnect an image containing a clear face.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// HeyGen Lipsync (re-sync lips to replacement audio)
// Speed variant: fal.ai $0.0500/sec → 24 credits/sec (~35% margin)
// Precision variant: fal.ai $0.1000/sec → 47 credits/sec (~35% margin)
const HEYGEN_LIPSYNC_DURATION_OPTIONS = [
  { value: '15', label: '~15 seconds' },
  { value: '30', label: '~30 seconds' },
  { value: '60', label: '~60 seconds' },
  { value: '120', label: '~2 minutes' },
  { value: '180', label: '~3 minutes' },
];

const HEYGEN_LIPSYNC_VARIANT_OPTIONS = [
  { value: 'heygen-lipsync-speed', label: 'Speed (fast, lower cost)' },
  { value: 'heygen-lipsync-precision', label: 'Precision (highest quality)' },
];

export const HEYGEN_LIPSYNC_NODE_DEF: UniversalNodeDefinition = {
  id: 'heygen-lipsync',
  type: 'HeyGenLipsync',
  title: 'HeyGen Lipsync',
  category: 'processor',
  variant: 'purple',
  // Accepts both a video (lip movement source) and an audio (replacement audio)
  acceptsMediaTypes: ['video', 'audio'],
  outputsMediaType: 'video',
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'lipsyncVariant',
      label: 'Variant',
      type: 'select',
      options: HEYGEN_LIPSYNC_VARIANT_OPTIONS,
      default: 'heygen-lipsync-speed',
      infoTooltip: 'Speed: faster + cheaper (24 credits/sec). Precision: highest quality (47 credits/sec).',
    },
    {
      id: 'duration',
      label: 'Video Duration (estimate)',
      type: 'select',
      options: HEYGEN_LIPSYNC_DURATION_OPTIONS,
      default: '30',
      infoTooltip: 'Estimated duration of the source video, used for credit reservation.',
    },
    {
      id: 'enableDynamicDuration',
      label: 'Adjust to audio length',
      type: 'boolean',
      default: true,
      infoTooltip: 'Allow output duration to adjust to match the replacement audio.',
    },
    {
      id: 'enableSpeechEnhancement',
      label: 'Enhance speech audio',
      type: 'boolean',
      default: false,
      infoTooltip: 'Apply speech enhancement to the replacement audio.',
    },
    {
      id: 'disableMusicTrack',
      label: 'Remove background music',
      type: 'boolean',
      default: false,
      infoTooltip: 'Strip the original video\'s background music.',
    },
    {
      id: 'enableCaption',
      label: 'Generate captions',
      type: 'boolean',
      default: false,
    },
  ],
  components: [
    {
      id: 'heygen-lipsync-prompt',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Connect a video (with the face to re-sync) and an audio output (the replacement audio).\nLip movement will be re-synced to the new audio automatically.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: false,
        minHeight: '60px',
        maxHeight: '120px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Output Node (displays as "Media" in UI)
export const OUTPUT_NODE_DEF: UniversalNodeDefinition = {
  id: 'output',
  type: 'Output',
  title: 'Media',
  category: 'output',
  variant: 'neutral',
  acceptsMediaTypes: ['text', 'image', 'video', 'audio'],
  outputsMediaType: 'any', // Outputs whatever media type it contains
  hasInputHandle: true,
  hasOutputHandle: true,
  components: [
    {
      id: 'output-display',
      type: 'PagedOutput',
      valueKey: 'content',
      onChangeKey: 'onPageChange',
      props: {
        variant: 'neutral',
        minHeight: '150px',
        maxHeight: '500px',
        content: '',
        pages: [],
        currentPageIndex: 0,
        onPageChange: () => {},
      },
    },
  ],
};

// Image Container Node (for uploading images and videos as canvas inputs)
export const IMAGE_PANEL_NODE_DEF: UniversalNodeDefinition = {
  id: 'image-panel',
  type: 'ImagePanel',
  title: 'Image Container',
  category: 'container',
  variant: 'neutral',
  outputsMediaType: 'image',
  hasInputHandle: false,
  hasOutputHandle: true,
  components: [
    {
      id: 'image-panel',
      type: 'ImagePanel',
      valueKey: 'imageUrl',
      onChangeKey: 'onImageChange',
      props: {
        imageUrl: '',
        onImageChange: () => {},
        variant: 'orange',
        targetWidth: 420,
      },
    },
  ],
};

// Inpaint Node (for image editing)
export const INPAINT_NODE_DEF: UniversalNodeDefinition = {
  id: 'inpaint',
  type: 'Inpaint',
  title: 'Inpaint',
  category: 'processor',
  variant: 'warning',
  hidden: true, // Hidden for now - feature not fully ready
  acceptsMediaTypes: ['image', 'text'],
  outputsMediaType: 'image',
  maxInputConnections: 2, // image + text prompt
  hasInputHandle: true,
  hasOutputHandle: true,
  components: [
    {
      id: 'inpaint-prompt',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      props: {
        placeholder: 'Describe how to modify the image...',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '200px',
        value: '',
        onChange: () => {},
      },
    },
    {
      id: 'inpaint-preview',
      type: 'MediaPreview',
      valueKey: 'imageUrl',
      props: {
        type: 'image',
        url: '',
        variant: 'orange',
        maxHeight: '400px',
        allowFullscreen: true,
      },
    },
  ],
};

// Prompt Node
export const DOCUMENT_PANEL_NODE_DEF: UniversalNodeDefinition = {
  id: 'document-panel',
  type: 'DocumentPanel',
  title: 'Prompt',
  category: 'container',
  variant: 'neutral',
  acceptsMediaTypes: [],
  outputsMediaType: 'text',
  hasInputHandle: false,
  hasOutputHandle: true,
  components: [
    {
      id: 'document-content',
      type: 'PagedOutput',
      valueKey: 'content',
      onChangeKey: 'onContentChange',
      props: {
        content: '',
        pages: [],
        currentPageIndex: 0,
        variant: 'neutral',
        onChange: () => {},
        onPageChange: () => {},
        placeholder: 'Enter your prompt...',
      },
    },
  ],
};

// Dictation Node (Speech-to-Text)
export const DICTATION_NODE_DEF: UniversalNodeDefinition = {
  id: 'dictation',
  type: 'Dictation',
  title: 'Talk to Text',
  category: 'source',
  variant: 'positive',
  outputsMediaType: 'text',
  hasInputHandle: false,
  hasOutputHandle: true,
  hidden: false,
  components: [
    {
      id: 'audio-recorder',
      type: 'AudioRecorder',
      valueKey: 'transcription',
      onChangeKey: 'onTranscription',
      props: {
        variant: 'positive',
        onRecordingComplete: () => {},
        onTranscription: () => {},
      },
    },
    {
      id: 'language-selector',
      type: 'LanguageSelector',
      valueKey: 'language',
      onChangeKey: 'onLanguageChange',
      props: {
        variant: 'positive',
        value: 'en',
        label: 'Spoken Language',
        onChange: () => {},
        languages: [
          { value: 'en', label: 'English' },
          { value: 'es', label: 'Spanish' },
          { value: 'fr', label: 'French' },
          { value: 'de', label: 'German' },
          { value: 'it', label: 'Italian' },
          { value: 'pt', label: 'Portuguese' },
          { value: 'nl', label: 'Dutch' },
          { value: 'ru', label: 'Russian' },
          { value: 'zh', label: 'Chinese' },
          { value: 'ja', label: 'Japanese' },
          { value: 'ko', label: 'Korean' },
          { value: 'ar', label: 'Arabic' },
          { value: 'hi', label: 'Hindi' },
        ],
      },
    },
  ],
};

// ImageScale Node — Resize or AI-enhance images
// Two modes: Lanczos (Sharp, free) and AI Enhance (fal.ai ESRGAN, costs credits)
export const IMAGE_SCALE_NODE_DEF: UniversalNodeDefinition = {
  id: 'image-scale',
  type: 'ImageScale',
  title: 'Image Scale',
  category: 'processor',
  variant: 'neutral',
  alwaysShowSettings: true,
  acceptsMediaTypes: ['image'],
  outputsMediaType: 'image',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Method',
      type: 'select',
      options: [
        { value: 'resize', label: 'Lanczos' },
        { value: 'ai-enhance', label: 'AI Enhance' },
      ],
      default: 'resize',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: 'original', label: 'Original' },
        { value: '1:1', label: 'Square 1:1' },
        { value: '16:9', label: 'Landscape 16:9' },
        { value: '5:4', label: 'Landscape 5:4' },
        { value: '4:3', label: 'Landscape 4:3' },
        { value: '9:16', label: 'Portrait 9:16' },
        { value: '4:5', label: 'Portrait 4:5' },
        { value: '3:4', label: 'Portrait 3:4' },
      ],
      default: 'original',
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      options: [
        { value: 'original', label: 'Original' },
        { value: '720p', label: '720p' },
        { value: '1080p', label: '1080p' },
        { value: '2k', label: '2K' },
        { value: '4k', label: '4K' },
        { value: 'custom', label: 'Custom' },
      ],
      default: 'original',
    },
    {
      id: 'targetWidth',
      label: 'Width',
      type: 'number',
      min: 1,
      max: 8192,
      step: 1,
      default: 1920,
      showWhen: { field: 'resolution', value: 'custom' },
    },
    {
      id: 'targetHeight',
      label: 'Height',
      type: 'number',
      min: 1,
      max: 8192,
      step: 1,
      default: 1080,
      showWhen: { field: 'resolution', value: 'custom' },
    },
    {
      id: 'fitMode',
      label: 'Fit',
      type: 'select',
      options: [
        { value: 'crop', label: 'Crop' },
        { value: 'stretch', label: 'Stretch' },
      ],
      default: 'crop',
      showWhen: { field: 'aspectRatio', value: 'original', operator: '!=' },
    },
    {
      id: 'cropAnchor',
      label: 'Crop Anchor',
      type: 'anchor',
      default: 'center',
      showWhen: { field: 'fitMode', value: 'crop' },
    },
  ],
  components: [],
};

// VideoTrim Node — Cut a segment from a video by setting start/end times
// Uses FFmpeg.wasm client-side (free, no server call)
export const VIDEO_TRIM_NODE_DEF: UniversalNodeDefinition = {
  id: 'video-trim',
  type: 'VideoTrim',
  title: 'Video Trim',
  category: 'processor',
  variant: 'neutral',
  alwaysShowSettings: true,
  acceptsMediaTypes: ['video'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'startTime',
      label: 'Start Time (seconds)',
      type: 'number',
      min: 0,
      max: 300,
      step: 0.1,
      default: 0,
    },
    {
      id: 'endTime',
      label: 'End Time (seconds)',
      type: 'number',
      min: 0,
      max: 300,
      step: 0.1,
      default: 5,
    },
  ],
  components: [],
};

// VideoSplit Node — Split a video into equal segments
// Uses FFmpeg.wasm client-side (free, no server call)
export const VIDEO_SPLIT_NODE_DEF: UniversalNodeDefinition = {
  id: 'video-split',
  type: 'VideoSplit',
  title: 'Video Split',
  category: 'processor',
  variant: 'neutral',
  alwaysShowSettings: true,
  acceptsMediaTypes: ['video'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'splitMode',
      label: 'Split Mode',
      type: 'select',
      options: [
        { value: 'equal', label: 'Equal Parts' },
        { value: 'seconds', label: 'Every N Seconds' },
      ],
      default: 'equal',
    },
    {
      id: 'numParts',
      label: 'Number of Parts',
      type: 'number',
      min: 2,
      max: 10,
      step: 1,
      default: 2,
      showWhen: { field: 'splitMode', value: 'equal' },
    },
    {
      id: 'segmentDuration',
      label: 'Segment Duration (seconds)',
      type: 'number',
      min: 1,
      max: 60,
      step: 1,
      default: 3,
      showWhen: { field: 'splitMode', value: 'seconds' },
    },
  ],
  components: [],
};

// VideoScale Node — Resize or AI-enhance videos
// Two modes: Simple Resize (FFmpeg, free) and AI Enhance (fal.ai Bytedance, costs credits)
export const VIDEO_SCALE_NODE_DEF: UniversalNodeDefinition = {
  id: 'video-scale',
  type: 'VideoScale',
  title: 'Video Scale',
  category: 'processor',
  variant: 'neutral',
  alwaysShowSettings: true,
  acceptsMediaTypes: ['video'],
  outputsMediaType: 'video',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Mode',
      type: 'select',
      options: [
        { value: 'resize', label: 'Resize (Free)' },
        { value: 'ai-enhance', label: 'AI Enhance (Credits)' },
      ],
      default: 'resize',
    },
    {
      id: 'targetResolution',
      label: 'Target Resolution',
      type: 'select',
      options: [
        { value: '480p', label: '480p' },
        { value: '720p', label: '720p' },
        { value: '1080p', label: '1080p' },
        { value: '1440p', label: '1440p (2K)' },
        { value: '4k', label: '4K' },
      ],
      default: '1080p',
      // AI enhance is an upscaler — fal.ai only accepts 1080p / 2k / 4k targets.
      conditionalOptions: {
        dependsOn: 'mode',
        rules: {
          'ai-enhance': [
            { value: '1080p', label: '1080p' },
            { value: '1440p', label: '1440p (2K)' },
            { value: '4k', label: '4K' },
          ],
        },
      },
    },
    {
      id: 'crop',
      label: 'Crop',
      type: 'select',
      options: [
        { value: 'none', label: 'No Crop (maintain aspect ratio)' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '9:16', label: '9:16 Portrait' },
        { value: '1:1', label: '1:1 Square' },
        { value: '4:3', label: '4:3 Classic' },
      ],
      default: 'none',
      // Crop only applies to the FFmpeg resize path; the AI upscaler maintains aspect ratio internally.
      showWhen: { field: 'mode', value: 'resize' },
    },
  ],
  components: [],
};

// VideoStitch Node — Concatenate 2+ videos into one
// Uses media-worker microservice with FFmpeg
export const VIDEO_STITCH_NODE_DEF: UniversalNodeDefinition = {
  id: 'video-stitch',
  type: 'VideoStitch',
  title: 'Video Stitch',
  category: 'processor',
  variant: 'neutral',
  alwaysShowSettings: true,
  acceptsMediaTypes: ['video'],
  outputsMediaType: 'video',
  hasInputHandle: false, // Uses labeled per-clip handles instead
  hasOutputHandle: true,
  settings: [
    {
      id: 'clipCount',
      label: 'Number of Clips',
      type: 'select',
      options: [
        { value: '2', label: '2 Clips' },
        { value: '3', label: '3 Clips' },
        { value: '4', label: '4 Clips' },
        { value: '5', label: '5 Clips' },
        { value: '6', label: '6 Clips' },
      ],
      default: '2',
    },
    {
      id: 'outputResolution',
      label: 'Output Resolution',
      type: 'select',
      options: [
        { value: 'match-first', label: 'Match First Video' },
        { value: '720p', label: '720p' },
        { value: '1080p', label: '1080p' },
      ],
      default: 'match-first',
    },
  ],
  components: [],
};

// Gemini Text Node (Google AI direct)
export const GEMINI_TEXT_NODE_DEF: UniversalNodeDefinition = {
  id: 'gemini-text',
  type: 'GeminiText',
  title: 'Gemini',
  category: 'processor',
  variant: 'positive',
  acceptsMediaTypes: ['text', 'image'], // Gemini text models are multimodal
  outputsMediaType: 'text',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Gemini Model',
      type: 'model',
      models: [
        // 3.6 Flash and 3.5 Flash-Lite both reached GA 2026-07-21.
        // 'gemini-3-flash-preview' was dropped from this list (superseded by
        // 3.6 Flash, and now pricier than 3.5 Flash-Lite) but is still in
        // MODEL_REGISTRY and still allowed server-side, so saved flows run.
        MODEL_REGISTRY['gemini-3.6-flash'],
        MODEL_REGISTRY['gemini-3.5-flash'],
        MODEL_REGISTRY['gemini-3.1-pro-preview'],
        MODEL_REGISTRY['gemini-3.5-flash-lite'],
        // 'gemini-3.1-flash-lite' removed: Google deprecated it 2026-05-07 with
        // a 2027-05-07 shutdown and names gemini-3.5-flash-lite (the line above)
        // as the replacement — https://ai.google.dev/gemini-api/docs/deprecations
        // NOT aliased: it still answers until shutdown, and 3.5 Flash-Lite is a
        // different price (2 cr vs 1 cr), so silently swapping it would change
        // what a saved flow bills. Kept in MODEL_REGISTRY and priced by the
        // CreditService fallback so saved flows keep pricing and running.
      ],
      // 3.6 Flash: same input price as 3.5 Flash, cheaper output.
      default: 'gemini-3.6-flash',
    },
  ],
  components: [
    {
      id: 'gemini-text-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Grok text (xAI direct). Multimodal like the Gemini node above — xAI reports a
// prompt_image_token_price for every text model, so images from upstream nodes
// are accepted as vision input.
export const GROK_TEXT_NODE_DEF: UniversalNodeDefinition = {
  id: 'grok-text',
  type: 'GrokText',
  title: 'Grok',
  category: 'processor',
  variant: 'positive',
  acceptsMediaTypes: ['text', 'image'],
  outputsMediaType: 'text',
  maxInputConnections: 10,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Grok Model',
      type: 'model',
      models: [
        MODEL_REGISTRY['grok-4.3'],
        MODEL_REGISTRY['grok-4.5'],
        MODEL_REGISTRY['grok-build-0.1'],
      ],
      // 4.3 is the default rather than 4.5: it has the LARGER context window
      // (1 M vs 500 k), costs less per call, and 4.5's premium is in reasoning
      // depth rather than the writing tasks this node is mostly used for.
      default: 'grok-4.3',
      infoTooltip: 'Grok 4.3 — 1M context, cheapest. Grok 4.5 — deeper reasoning, 500k context. Grok Build — code-focused, 256k context.',
    },
  ],
  components: [
    {
      id: 'grok-text-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Enter writing instructions... Press Ctrl+Enter to execute',
        variant: 'positive',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '100px',
        maxHeight: '400px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// ============================================================================
// AUDIO GENERATORS (ElevenLabs) — the FIRST nodes with outputsMediaType 'audio'
// ============================================================================
// Direct ElevenLabs integration via /api/elevenlabs (platform ELEVENLABS_API_KEY).
// Text → audio. Output renders in the Output node (MediaPreview handles 'audio').

// Voice / Text-to-Speech. Default model = eleven_v3 (flagship, GA on the API).
export const VOICE_TTS_NODE_DEF: UniversalNodeDefinition = {
  id: 'voice-tts',
  type: 'VoiceTTS',
  title: 'Voice',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'audio',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model',
      type: 'select',
      options: [
        { value: 'eleven_v3', label: 'Eleven v3 (Most Expressive)' },
        { value: 'eleven_flash_v2_5', label: 'Flash v2.5 (Fastest / Cheapest)' },
        { value: 'eleven_multilingual_v2', label: 'Multilingual v2 (Stable)' },
        // Removed from the dropdown 2026-09: eleven_turbo_v2_5 is deprecated
        // upstream (superseded by eleven_flash_v2_5) and was strictly redundant
        // here — same 23 cr / 1,000 chars as Flash v2.5, so it could only ever
        // be the worse pick. Following the migration-106 pattern it stays
        // priced and callable for flows already saved with it: creditCostUtils
        // MODEL_COSTS + calculateElevenLabsTTSCreditCost, CreditService
        // FALLBACK_MODEL_COSTS + TTS_CREDITS_PER_1K_CHARS, the modelConfigs
        // character cap, and the server VALID_TTS_MODELS allowlist all keep it.
      ],
      default: 'eleven_v3',
      infoTooltip: 'Eleven v3 is the most expressive model (70+ languages). Flash v2.5 is ~50% cheaper per character.',
    },
    {
      id: 'voice',
      label: 'Voice',
      type: 'select',
      // Curated ElevenLabs public "Default" voices (IDs verified Jul 2026).
      // NOTE: classic Default voices retire 2026-12-31 — revisit before then.
      options: [
        { value: 'JBFqnCBsd6RMkjVDRZzb', label: 'George (Warm, British)' },
        { value: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel (Calm, American)' },
        { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (Soft, American)' },
        { value: 'AZnzlk1XvdvUeBnXmlld', label: 'Domi (Strong, American)' },
        { value: 'ErXwobaYiN019PkySvjV', label: 'Antoni (Well-rounded)' },
      ],
      default: 'JBFqnCBsd6RMkjVDRZzb',
    },
    {
      id: 'format',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'mp3_44100_128', label: 'MP3 44.1kHz 128kbps' },
        { value: 'mp3_44100_192', label: 'MP3 44.1kHz 192kbps' },
        { value: 'pcm_44100', label: 'PCM 44.1kHz (WAV)' },
      ],
      default: 'mp3_44100_128',
    },
    {
      id: 'stability',
      label: 'Stability',
      type: 'slider',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.5,
      infoTooltip: 'Lower = more expressive/variable, higher = more stable/monotone.',
    },
    {
      id: 'similarityBoost',
      label: 'Similarity',
      type: 'slider',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.75,
      infoTooltip: 'How closely the output adheres to the original voice.',
    },
  ],
  components: [
    {
      id: 'voice-tts-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Type the words to speak... or connect a text node.',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: false,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Sound Effects. Text → audio via /v1/sound-generation.
export const SOUND_EFFECTS_NODE_DEF: UniversalNodeDefinition = {
  id: 'sound-effects',
  type: 'SoundEffects',
  title: 'Sound Effects',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'audio',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { value: 'auto', label: 'Auto (from prompt)' },
        { value: '2', label: '2 seconds' },
        { value: '5', label: '5 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '15', label: '15 seconds' },
        { value: '22', label: '22 seconds' },
      ],
      default: 'auto',
      infoTooltip: 'Target length (0.5–30s), or Auto to let ElevenLabs infer it.',
    },
    {
      id: 'promptInfluence',
      label: 'Prompt Influence',
      type: 'slider',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.3,
      infoTooltip: 'Higher values follow the prompt more literally.',
    },
    {
      id: 'format',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'mp3_44100_128', label: 'MP3 44.1kHz 128kbps' },
        { value: 'mp3_44100_192', label: 'MP3 44.1kHz 192kbps' },
      ],
      default: 'mp3_44100_128',
    },
  ],
  components: [
    {
      id: 'sound-effects-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe a sound... e.g. "thunder rolling over a distant city".',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: false,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// Music. Text → audio via /v1/music. Default model = music_v2.
// music_v1 is deprecated upstream (superseded by music_v2) but stays in the
// dropdown and in both cost tables so flows already saved with it keep
// resolving a price instead of throwing "Model not found".
export const MUSIC_NODE_DEF: UniversalNodeDefinition = {
  id: 'music',
  type: 'Music',
  title: 'Music',
  category: 'processor',
  variant: 'orange',
  acceptsMediaTypes: ['text'],
  outputsMediaType: 'audio',
  maxInputConnections: 1,
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'selectedModel',
      label: 'Model',
      type: 'select',
      options: [
        { value: 'music_v2', label: 'Eleven Music v2' },
        { value: 'music_v1', label: 'Eleven Music v1 (Legacy)' },
      ],
      default: 'music_v2',
    },
    {
      id: 'duration',
      label: 'Length',
      type: 'select',
      options: [
        { value: 'auto', label: 'Auto' },
        { value: '10', label: '10 seconds' },
        { value: '20', label: '20 seconds' },
        { value: '30', label: '30 seconds' },
        { value: '60', label: '1 minute' },
        { value: '120', label: '2 minutes' },
      ],
      default: '30',
      infoTooltip: 'Target song length (3s–10min), or Auto to let ElevenLabs decide.',
    },
    {
      id: 'format',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'mp3_44100_128', label: 'MP3 44.1kHz 128kbps' },
        { value: 'mp3_44100_192', label: 'MP3 44.1kHz 192kbps' },
      ],
      default: 'mp3_44100_128',
    },
  ],
  components: [
    {
      id: 'music-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe the music... e.g. "upbeat lo-fi hip-hop with warm piano".',
        variant: 'orange',
        showSubmitButton: true,
        showMagicWand: false,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

// MiniMax H3 Video Node (fal.ai) — a.k.a. Hailuo 03. Distinct, newer family
// from the MinimaxVideo node above (hailuo-02 / hailuo-2.3).
// T2V, I2V and first-last-frame (FFLF is the I2V endpoint + end_image_url).
// 5-15s at 2K/24fps. Native stereo audio is MANDATORY and has no toggle
// parameter in any H3 schema — deliberately no `enableAudio` setting here.
// No `seed` parameter exists either. aspect_ratio is absent from the I2V
// schema entirely, hence the showWhen on text-to-video only.
export const MINIMAX_H3_VIDEO_NODE_DEF: UniversalNodeDefinition = {
  id: 'minimax-h3-video',
  type: 'MinimaxH3Video',
  title: 'MiniMax H3',
  category: 'processor',
  variant: 'purple',
  acceptsMediaTypes: ['text', 'image'],  // order matters: handles[0] must be text
  outputsMediaType: 'video',
  maxInputConnections: 2,  // Up to 2 images for first-last-frame mode
  hasInputHandle: true,
  hasOutputHandle: true,
  settings: [
    {
      id: 'mode',
      label: 'Generation Mode',
      type: 'select',
      // Three options, and the values are the literal strings matched across
      // the canvas, the adapter and server/routes/falai.ts. A two-option select
      // renders in the prompt modal as an unlabeled cycle button.
      options: [
        { value: 'text-to-video', label: 'Text-to-Video' },
        { value: 'image-to-video', label: 'Image-to-Video (1 image)' },
        { value: 'first-last-frame', label: 'First-to-Last Frame (2 images)' },
      ],
      default: 'image-to-video',
    },
    {
      id: 'aspectRatio',
      label: 'Aspect Ratio',
      type: 'select',
      options: [
        { value: '21:9', label: '21:9 Cinemascope' },
        { value: '16:9', label: '16:9 Landscape' },
        { value: '4:3', label: '4:3 Classic' },
        { value: '1:1', label: '1:1 Square' },
        { value: '3:4', label: '3:4 Portrait' },
        { value: '9:16', label: '9:16 Portrait' },
      ],
      default: '16:9',
      // aspect_ratio does not exist on minimax/h3/image-to-video — the output
      // follows the input image. Only meaningful for text-to-video.
      showWhen: { field: 'mode', value: 'text-to-video', operator: '=' },
    },
    {
      id: 'resolution',
      label: 'Resolution',
      type: 'select',
      // FOUR tiers, and the values are fal's enum spelled EXACTLY as fal spells
      // it — capital P on 480P and 768P. fal's enum is case-sensitive and it
      // discards an unrecognised value silently, rendering at its 2K default;
      // server/routes/falai.ts canonicalises and validates before reserving, so
      // a wrong spelling here fails loudly rather than billing for a render
      // that never happened.
      //
      // The labels name the upscale relationship because it changes which tier
      // a user wants: 768P is the model's top NATIVE mode, and 2K/4K are
      // upscales of a 768P base — they buy pixels, not detail, at 2.1x and 2.6x
      // the price. fal, verbatim: "480P and 768P are native generation modes;
      // 2K and 4K upscale a 768P base result."
      //
      // Dropdown order is cheapest-first so the price ladder reads downward.
      // The DEFAULT stays '2K' deliberately: it is fal's own default and what
      // every already-saved flow has stored, so this change does not move a
      // single existing generation's output or its bill.
      options: [
        { value: '480P', label: '480P (native)' },
        { value: '768P', label: '768P (native)' },
        { value: '2K', label: '2K (768P upscaled)' },
        { value: '4K', label: '4K (768P upscaled)' },
      ],
      default: '2K',
    },
    {
      id: 'duration',
      label: 'Duration',
      type: 'select',
      // The wire format is a plain INTEGER 5..15 (not a string, not an enum);
      // the coercion lives in server/routes/falai.ts and ProviderService.
      options: [
        { value: '5', label: '5 seconds' },
        { value: '6', label: '6 seconds' },
        { value: '8', label: '8 seconds' },
        { value: '10', label: '10 seconds' },
        { value: '12', label: '12 seconds' },
        { value: '15', label: '15 seconds' },
      ],
      default: '5',
    },
    {
      id: 'batchCount',
      label: 'Batch Generations',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 1,
      infoTooltip: 'Number of videos to generate (1-10)',
    },
    {
      id: 'batchOutputMode',
      label: 'Output',
      type: 'select',
      options: [
        { value: 'single', label: 'New Page' },
        { value: 'multiple', label: 'New Node' },
      ],
      default: 'single',
      infoTooltip: 'How to output multiple generations: new page in same output, or separate output nodes',
    },
  ],
  components: [
    {
      id: 'video-prompt-textarea',
      type: 'MarkdownTextarea',
      valueKey: 'prompt',
      onChangeKey: 'onPromptChange',
      onSubmitKey: 'onSubmit',
      props: {
        placeholder: 'Describe your video... MiniMax H3 generates synchronized stereo audio in the same pass.',
        variant: 'purple',
        showSubmitButton: true,
        showMagicWand: true,
        minHeight: '80px',
        maxHeight: '300px',
        value: '',
        onChange: () => {},
      },
    },
  ],
};

/**
 * Node Definitions Registry
 * Maps node type to its definition
 */
export const NODE_DEFINITIONS: Record<string, UniversalNodeDefinition> = {
  'StartingPrompt': STARTING_PROMPT_NODE_DEF,
  'AgentPrompt': AGENT_PROMPT_NODE_DEF,
  'ChatGPT': CHATGPT_NODE_DEF,
  'Claude': CLAUDE_NODE_DEF,
  'Kimi': KIMI_NODE_DEF,
  'Qwen': QWEN_NODE_DEF,
  'Minimax': MINIMAX_NODE_DEF,
  'GeminiText': GEMINI_TEXT_NODE_DEF,
  'GrokText': GROK_TEXT_NODE_DEF,
  'ImagePrompt': IMAGE_PROMPT_NODE_DEF,
  'GPTImage': GPT_IMAGE_NODE_DEF,
  'FluxImage': FLUX_IMAGE_NODE_DEF,
  // fal.ai Image Nodes
  'ZImageTurbo': Z_IMAGE_TURBO_NODE_DEF,
  'QwenImage': QWEN_IMAGE_NODE_DEF,
  'QwenImageEdit': QWEN_IMAGE_EDIT_NODE_DEF,
  'Seedream': SEEDREAM_NODE_DEF,
  'KlingImage': KLING_IMAGE_NODE_DEF,
  'GLMImage': GLM_IMAGE_NODE_DEF,
  'RecraftV3': RECRAFT_V3_NODE_DEF,
  'GrokImage': GROK_IMAGE_NODE_DEF,
  // Video Nodes
  // fal.ai Video Nodes
  'KlingVideo': KLING_VIDEO_NODE_DEF,
  'Kling26Video': KLING_26_VIDEO_NODE_DEF,
  'Kling25Video': KLING_25_VIDEO_NODE_DEF,
  'KlingO3Video': KLING_O3_VIDEO_NODE_DEF,
  'WanVideo': WAN_VIDEO_NODE_DEF,
  'Wan26Video': WAN26_VIDEO_NODE_DEF,
  'WanAnimateVideo': WAN_ANIMATE_VIDEO_NODE_DEF,
  'LTXVideo': LTX_VIDEO_NODE_DEF,
  'LTXFastVideo': LTX_FAST_VIDEO_NODE_DEF,
  'PixVerseVideo': PIXVERSE_VIDEO_NODE_DEF,
  'HunyuanVideo': HUNYUAN_VIDEO_NODE_DEF,
  'MinimaxVideo': MINIMAX_VIDEO_NODE_DEF,
  'KlingAvatar': KLING_AVATAR_NODE_DEF,
  'VeoVideo': VEO_VIDEO_NODE_DEF,
  'GeminiOmni': GEMINI_OMNI_NODE_DEF,
  'GrokVideo': GROK_VIDEO_NODE_DEF,
  'Seedance2Video': SEEDANCE2_VIDEO_NODE_DEF,
  'Seedance25Video': SEEDANCE_25_VIDEO_NODE_DEF,
  'MinimaxH3Video': MINIMAX_H3_VIDEO_NODE_DEF,
  'HeyGenAvatarV3': HEYGEN_AVATAR_V3_NODE_DEF,
  'HeyGenAvatarV4': HEYGEN_AVATAR_V4_NODE_DEF,
  'HeyGenAvatarV': HEYGEN_AVATAR_V_NODE_DEF,
  'HeyGenVideoAgent': HEYGEN_VIDEO_AGENT_NODE_DEF,
  'HeyGenTranslatePrecision': HEYGEN_TRANSLATE_PRECISION_NODE_DEF,
  'HeyGenTranslateSpeed': HEYGEN_TRANSLATE_SPEED_NODE_DEF,
  'HeyGenPhotoVideo': HEYGEN_PHOTO_VIDEO_NODE_DEF,
  'HeyGenLipsync': HEYGEN_LIPSYNC_NODE_DEF,
  // Audio Nodes (ElevenLabs) — first audio-output generators
  'VoiceTTS': VOICE_TTS_NODE_DEF,
  'SoundEffects': SOUND_EFFECTS_NODE_DEF,
  'Music': MUSIC_NODE_DEF,
  'Output': OUTPUT_NODE_DEF,
  'ImagePanel': IMAGE_PANEL_NODE_DEF,
  'Inpaint': INPAINT_NODE_DEF,
  'DocumentPanel': DOCUMENT_PANEL_NODE_DEF,
  'Dictation': DICTATION_NODE_DEF,
  'ImageScale': IMAGE_SCALE_NODE_DEF,
  'VideoTrim': VIDEO_TRIM_NODE_DEF,
  'VideoSplit': VIDEO_SPLIT_NODE_DEF,
  'VideoScale': VIDEO_SCALE_NODE_DEF,
  'VideoStitch': VIDEO_STITCH_NODE_DEF,
};

/**
 * Get node definition by type
 */
export function getNodeDefinition(type: string): UniversalNodeDefinition | undefined {
  return NODE_DEFINITIONS[type];
}

/**
 * Get all node definitions
 */
export function getAllNodeDefinitions(): UniversalNodeDefinition[] {
  return Object.values(NODE_DEFINITIONS);
}

/**
 * Get node definitions by category
 */
export function getNodeDefinitionsByCategory(
  category: 'source' | 'processor' | 'output'
): UniversalNodeDefinition[] {
  return getAllNodeDefinitions().filter(def => def.category === category);
}
